import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PredictiveModule from './modules/PredictiveModule';
import BenchmarkingModule from './modules/BenchmarkingModule';
import { useTranslations } from './hooks/useTranslations';

// Currency formatting functions for different locales
const formatCurrency = (value, language = 'es') => {
  const locale = language === 'en' ? 'en-US' : 'es-ES';
  return new Intl.NumberFormat(locale, { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatNumber = (value, language = 'es') => {
  const locale = language === 'en' ? 'en-US' : 'es-ES';
  return new Intl.NumberFormat(locale).format(value);
};

// Componente Tooltip MOVIDO FUERA para evitar re-creación
const Tooltip = ({ text, children }) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 max-w-xs">
      {text}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
    </div>
  </div>
);

// Componente Input con validación MOVIDO FUERA y optimizado con React.memo
const ValidatedInput = React.memo(({ 
  type = 'text', 
  value, 
  onChange, 
  placeholder, 
  required = false, 
  fieldName,
  tooltip,
  label,
  options = null,
  validationErrors = {},
  min,
  max,
  step,
  t // Adding translation function as prop
}) => {
  const hasError = validationErrors[fieldName];
  
  // PROBLEMA 3 SOLUCIONADO: Mensajes personalizados internacionalizados
  const setCustomErrorMessage = (element, fieldName) => {
    if (!element || !t) return;
    
    element.addEventListener('invalid', function(e) {
      e.preventDefault();
      
      if (element.validity.valueMissing) {
        element.setCustomValidity(t('home.calculator.validation.required'));
      } else if (element.validity.rangeUnderflow) {
        element.setCustomValidity(t('home.calculator.validation.minValue', { min: element.min }));
      } else if (element.validity.rangeOverflow) {
        if (fieldName === 'recurrentShare') {
          element.setCustomValidity(t('home.calculator.validation.invalidRecurrentShare'));
        } else {
          element.setCustomValidity(t('home.calculator.validation.maxValue', { max: element.max }));
        }
      } else if (element.validity.stepMismatch) {
        element.setCustomValidity(t('home.calculator.validation.invalidValue'));
      } else if (element.validity.typeMismatch) {
        element.setCustomValidity(t('home.calculator.validation.invalidFormat'));
      } else {
        element.setCustomValidity('');
      }
    });
    
    element.addEventListener('input', function() {
      element.setCustomValidity('');
    });
  };
  
  // 🔍 DIAGNOSTIC LOGGING - Mount/Unmount/Updates
  React.useEffect(() => {
    console.log(`🔍 ValidatedInput ${fieldName} mounted/updated`, { 
      value, 
      hasError, 
      timestamp: Date.now()
    });
    return () => console.log(`🗑️ ValidatedInput ${fieldName} unmounting`);
  }, [fieldName, value, hasError]);
  
  // 🔍 DIAGNOSTIC LOGGING - Focus/Blur handlers
  const handleFocus = React.useCallback((e) => {
    console.log(`👁️ FOCUS: ${fieldName}`, {
      value: e.target.value,
      placeholder: e.target.placeholder,
      timestamp: Date.now()
    });
    setCustomErrorMessage(e.target, fieldName);
  }, [fieldName]);
  
  const handleBlur = React.useCallback((e) => {
    console.log(`😴 BLUR: ${fieldName}`, {
      value: e.target.value,
      placeholder: e.target.placeholder,
      timestamp: Date.now()
    });
    // Validar al perder el foco
    if (e.target.checkValidity) {
      e.target.checkValidity();
    }
  }, [fieldName]);
  
  // 🔍 DIAGNOSTIC LOGGING - onChange wrapper
  const handleChange = React.useCallback((e) => {
    console.log(`📝 CHANGE: ${fieldName}`, {
      value: e.target.value,
      valueLength: e.target.value.length,
      focused: document.activeElement === e.target,
      timestamp: Date.now()
    });
    
    const element = e.target;
    
    // Limpiar mensaje personalizado al cambiar
    element.setCustomValidity('');
    
    // PROBLEMA 2: Validación específica para porcentaje de ingresos recurrentes
    if (fieldName === 'recurrentShare') {
      const value = parseFloat(e.target.value);
      if (value > 100) {
        element.setCustomValidity(t ? t('home.calculator.validation.invalidRecurrentShare') : 'Invalid percentage');
        element.reportValidity();
        return;
      }
    }
    
    onChange(e);
  }, [fieldName, onChange]);

  // Determinar atributos de validación específicos por campo
  const getValidationProps = () => {
    const props = {
      min: min,
      max: max,
      step: step
    };

    // PROBLEMA 2 y 5: Validaciones específicas por campo
    switch (fieldName) {
      case 'recurrentShare':
        return { ...props, min: '0', max: '100', step: '1' };
      case 'revenue':
      case 'personalRevenue':
        return { ...props, min: '0', max: '100000000', step: 'any' };
      case 'clients':
        return { ...props, min: '0', max: '1000000', step: '1' };
      case 'yearsInMarket':
        return { ...props, min: '0', max: '100', step: '1' };
      case 'socialFollowers':
        return { ...props, min: '0', max: '100000000', step: '1' };
      case 'monthlyTraffic':
        return { ...props, min: '0', max: '100000000', step: '1' };
      case 'averageOrderValue':
        return { ...props, min: '0', max: '100000', step: 'any' };
      case 'conversionRate':
        return { ...props, min: '0', max: '100', step: '0.1' };
      case 'churnRate':
        return { ...props, min: '0', max: '100', step: '0.1' };
      default:
        return props;
    }
  };

  const validationProps = type === 'number' ? getValidationProps() : {};
  
  // Asegurar que value nunca sea undefined/null para evitar el warning de React
  const safeValue = value || '';
  
  // Estilos corregidos para select
  const inputClasses = `w-full px-4 py-3 bg-slate-700/50 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
    hasError 
      ? 'border-red-500 focus:ring-red-500 text-white' 
      : 'border-slate-600 focus:ring-purple-500 text-white'
  } ${options ? 'text-white bg-slate-700/50' : 'text-white placeholder-gray-400'}`;

  const selectClasses = `w-full px-4 py-3 bg-slate-700/50 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
    hasError 
      ? 'border-red-500 focus:ring-red-500' 
      : 'border-slate-600 focus:ring-purple-500'
  } ${!safeValue ? 'text-gray-400' : 'text-white'}`;

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <label className="block text-sm font-medium text-gray-300">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
        {tooltip && (
          <Tooltip text={tooltip}>
            <div className="text-gray-400 hover:text-white cursor-help">ℹ️</div>
          </Tooltip>
        )}
      </div>
      
      {options ? (
        <select
          value={safeValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={selectClasses}
          required={required}
        >
          <option value="" disabled hidden className="text-gray-400">
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value} className="text-white bg-slate-800">
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={safeValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={inputClasses}
          placeholder={placeholder}
          required={required}
          {...validationProps}
        />
      )}
      
      {hasError && (
        <p className="text-red-400 text-sm">{hasError}</p>
      )}
    </div>
  );
});

// LeadCaptureForm component moved outside to prevent re-creation
const LeadCaptureForm = ({ leadData, setLeadData, setLeadId, setCurrentStep }) => {
  const { t } = useTranslations();
  // Estados locales para evitar re-renders del componente padre
  const [localName, setLocalName] = useState(leadData.name);
  const [localEmail, setLocalEmail] = useState(leadData.email);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Sincronizar con el estado principal solo cuando sea necesario
  useEffect(() => {
    setLocalName(leadData.name);
    setLocalEmail(leadData.email);
  }, [leadData.name, leadData.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Actualizar estado principal
    setLeadData({ name: localName, email: localEmail });

    try {
      // Primero, suscribir a MailerLite
      try {
        const subscriptionResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: localName, email: localEmail }),
        });

        if (subscriptionResponse.ok) {
          console.log('Successfully subscribed to MailerLite');
        } else {
          console.warn('MailerLite subscription failed, but continuing with calculator access');
        }
      } catch (mailerliteError) {
        console.warn('MailerLite subscription error:', mailerliteError);
        // No impedimos el acceso a la calculadora si falla MailerLite
      }

      // Luego, crear el lead en nuestra base de datos (mantenemos funcionalidad existente)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: localName, email: localEmail }),
      });

      if (!response.ok) {
        throw new Error('Error al guardar la información');
      }

      const result = await response.json();
      setLeadId(result.id);
      
      // Guardar estado inicial una vez que tenemos leadId
      const initialState = {
        currentStep: 'brandType',
        leadData: { name: localName, email: localEmail },
        leadId: result.id,
        brandType: '',
        formData: {
          companyName: '', personalName: '', revenue: '', personalRevenue: '',
          industry: '', personalNiche: '', clients: '', yearsInMarket: '',
          socialFollowers: '', monthlyTraffic: '', tangibleAssets: '',
          brandRecognition: '', marketPosition: '',
          twitterProfile: '', instagramProfile: '', facebookProfile: '',
          linkedinProfile: '', hashtags: '', sentimentConsent: false
        }
      };
      localStorage.setItem('calculatorState', JSON.stringify(initialState));
      
      setCurrentStep('brandType');
    } catch (error) {
      setError('Error al procesar la información. Por favor intenta nuevamente.');
      console.error('Error submitting lead:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto text-center px-4">
      <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-3xl p-6 sm:p-8 mb-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          {t('calculatorAccess.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">{t('calculatorAccess.titleHighlight')}</span>
        </h2>
        <p className="text-lg sm:text-xl text-gray-300 mb-8">
          {t('calculatorAccess.subtitle')}
        </p>
        
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-8">
          <div className="flex items-center space-x-2">
            <div className="text-green-400">✓</div>
            <span className="text-gray-300 text-sm sm:text-base">{t('calculatorAccess.features.completeAnalysis')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-green-400">✓</div>
            <span className="text-gray-300 text-sm sm:text-base">{t('calculatorAccess.features.instantResults')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-green-400">✓</div>
            <span className="text-gray-300 text-sm sm:text-base">{t('calculatorAccess.features.free')}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300 text-left">
              {t('calculatorAccess.form.fullName')} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300"
              placeholder={t('calculatorAccess.form.fullNamePlaceholder')}
              required
              autoComplete="name"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300 text-left">
              {t('calculatorAccess.form.email')} <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={localEmail}
              onChange={(e) => setLocalEmail(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300"
              placeholder={t('calculatorAccess.form.emailPlaceholder')}
              required
              autoComplete="email"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 max-w-2xl mx-auto">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="max-w-md mx-auto">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>{t('calculatorAccess.form.processing')}</span>
              </div>
            ) : (
              t('calculatorAccess.form.startButton')
            )}
          </button>
        </div>
      </form>

      <p className="text-gray-400 text-sm mt-4">
        {t('home.calculator.disclaimer')}
      </p>
    </div>
  );
};

// Step1Component moved outside and simplified - NO HOOKS ALLOWED
const Step1Component = React.memo(function Step1Component({
  brandType,
  formData,
  validationErrors,
  tooltips,
  onTextChange,
  onNumericChange,
  onSelectChange,
  t, // Add translation function as prop
}) {
  // ❌ ZERO HOOKS - Component is purely functional
  
  const industryOptions = [
    { value: 'technology', label: t('home.calculator.options.industry.technology') },
    { value: 'ecommerce', label: t('home.calculator.options.industry.ecommerce') },
    { value: 'consulting', label: t('home.calculator.options.industry.consulting') },
    { value: 'healthcare', label: t('home.calculator.options.industry.healthcare') },
    { value: 'education', label: t('home.calculator.options.industry.education') },
    { value: 'finance', label: t('home.calculator.options.industry.finance') },
    { value: 'manufacturing', label: t('home.calculator.options.industry.manufacturing') },
    { value: 'real_estate', label: t('home.calculator.options.industry.realEstate') },
    { value: 'hospitality', label: t('home.calculator.options.industry.hospitality') },
    { value: 'marketing', label: t('home.calculator.options.industry.marketing') },
    { value: 'other', label: t('home.calculator.options.industry.other') }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
          {brandType === 'company' ? t('home.calculator.sections.companyBasicInfo') : t('home.calculator.sections.personalBasicInfo')}
        </h3>
        <p className="text-gray-300 text-lg">
          {t('home.calculator.fields.businessFundamentals')}
        </p>
      </div>

      <div className="phase1-card-grid grid grid-cols-1 gap-8">
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <h4 className="text-xl font-bold text-white mb-6 flex items-center">
            <span className="text-2xl mr-3">🏢</span>
            {t('home.calculator.sections.mainData')}
          </h4>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ValidatedInput
                label={brandType === 'company' ? t('home.calculator.fields.companyName') : t('home.calculator.fields.personalName')}
                value={brandType === 'company' ? (formData.companyName ?? '') : (formData.personalName ?? '')}
                onChange={(e) => onTextChange(brandType === 'company' ? 'companyName' : 'personalName', e.target.value)}
                placeholder={brandType === 'company' ? 'Ej: TechCorp Solutions' : 'Ej: María García'}
                required={true}
                fieldName={brandType === 'company' ? 'companyName' : 'personalName'}
                validationErrors={validationErrors}
                t={t}
              />
              
              <ValidatedInput
                type="number"
                label={t('home.calculator.fields.annualRevenue')}
                value={brandType === 'company' ? (formData.revenue ?? '') : (formData.personalRevenue ?? '')}
                onChange={(e) => onNumericChange(brandType === 'company' ? 'revenue' : 'personalRevenue', e.target.value)}
                placeholder="Ej: 500000"
                required={true}
                fieldName={brandType === 'company' ? 'revenue' : 'personalRevenue'}
                tooltip={tooltips[brandType === 'company' ? 'revenue' : 'personalRevenue']}
                validationErrors={validationErrors}
                t={t}
              />
            </div>

            <ValidatedInput
              label={t('home.calculator.fields.activitySector')}
              value={brandType === 'company' ? (formData.industry ?? '') : (formData.personalNiche ?? '')}
              onChange={(e) => onSelectChange(brandType === 'company' ? 'industry' : 'personalNiche', e.target.value)}
              placeholder={t('home.calculator.fields.selectSector')}
              options={industryOptions}
              required={true}
              fieldName={brandType === 'company' ? 'industry' : 'personalNiche'}
              tooltip={tooltips[brandType === 'company' ? 'industry' : 'personalNiche']}
              validationErrors={validationErrors}
            />

            {/* Nuevos campos opcionales de alto impacto */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ValidatedInput
                label={t('calculator.step1.businessModel')}
                value={formData.businessModel ?? ''}
                onChange={(e) => onSelectChange('businessModel', e.target.value)}
                placeholder={t('calculator.step1.placeholders.businessModel')}
                options={[
                  { value: 'b2b', label: t('calculator.step1.businessModelOptions.b2b') },
                  { value: 'b2c', label: t('calculator.step1.businessModelOptions.b2c') },
                  { value: 'b2b2c', label: t('calculator.step1.businessModelOptions.b2b2c') },
                  { value: 'd2c', label: t('calculator.step1.businessModelOptions.d2c') },
                  { value: 'saas', label: t('calculator.step1.businessModelOptions.saas') },
                  { value: 'marketplace', label: t('calculator.step1.businessModelOptions.marketplace') },
                  { value: 'ecommerce', label: t('calculator.step1.businessModelOptions.ecommerce') }
                ]}
                fieldName="businessModel"
                tooltip={t('calculator.step1.tooltips.businessModel')}
                validationErrors={validationErrors}
              />

              <ValidatedInput
                label={t('calculator.step1.customerSegment')}
                value={formData.customerSegment ?? ''}
                onChange={(e) => onSelectChange('customerSegment', e.target.value)}
                placeholder={t('calculator.step1.placeholders.customerSegment')}
                options={[
                  { value: 'smb', label: t('calculator.step1.customerSegmentOptions.smb') },
                  { value: 'midMarket', label: t('calculator.step1.customerSegmentOptions.midMarket') },
                  { value: 'enterprise', label: t('calculator.step1.customerSegmentOptions.enterprise') }
                ]}
                fieldName="customerSegment"
                tooltip={t('calculator.step1.tooltips.customerSegment')}
                validationErrors={validationErrors}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ValidatedInput
                label={t('calculator.step1.revenueModel')}
                value={formData.revenueModel ?? ''}
                onChange={(e) => onSelectChange('revenueModel', e.target.value)}
                placeholder={t('calculator.step1.placeholders.revenueModel')}
                options={[
                  { value: 'recurrent', label: t('calculator.step1.revenueModelOptions.recurrent') },
                  { value: 'transactional', label: t('calculator.step1.revenueModelOptions.transactional') },
                  { value: 'mixed', label: t('calculator.step1.revenueModelOptions.mixed') }
                ]}
                fieldName="revenueModel"
                tooltip={t('calculator.step1.tooltips.revenueModel')}
                validationErrors={validationErrors}
              />

              <ValidatedInput
                type="number"
                label={t('calculator.step1.recurrentShare')}
                value={formData.recurrentShare ?? ''}
                onChange={(e) => onNumericChange('recurrentShare', e.target.value)}
                placeholder={t('calculator.step1.placeholders.recurrentShare')}
                fieldName="recurrentShare"
                tooltip={t('calculator.step1.tooltips.recurrentShare')}
                validationErrors={validationErrors}
              />
            </div>

            {/* Referencias Sectoriales Visibles */}
            <div className="mt-4 p-4 bg-blue-900/20 border border-blue-500/30 rounded-xl">
              <div className="flex items-start">
                <div className="text-blue-400 text-lg mr-3 mt-0.5">💼</div>
                <div>
                  <p className="text-blue-300 text-sm leading-relaxed">
                    {t('home.calculator.sectorMultipliers') || 'Multiplicadores típicos: Tecnología (3-8x), Consultoría (2-4x), E-commerce (2.5-5x). Basados en transacciones reales del mercado español y latinoamericano.'}
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Campos específicos por sector */}
      {formData.industry && (
        <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 rounded-2xl p-6 border border-purple-500/20">
          <h4 className="text-xl font-bold text-white mb-4 flex items-center">
            <span className="text-2xl mr-3">🎯</span>
            {t('home.calculator.fields.sectorInfo')}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formData.industry === 'technology' && (
              <>
                <ValidatedInput
                  type="number"
                  label="Puntuación de Innovación Tech (1-10)"
                  value={formData.techInnovationScore ?? ''}
                  onChange={(e) => onNumericChange('techInnovationScore', e.target.value)}
                  placeholder="Ej: 8"
                  fieldName="techInnovationScore"
                  tooltip={tooltips.techInnovationScore}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  label="Stack Tecnológico Principal"
                  value={formData.techStack ?? ''}
                  onChange={(e) => onTextChange('techStack', e.target.value)}
                  placeholder="Ej: React, Node.js, AWS"
                  fieldName="techStack"
                  tooltip={tooltips.techStack}
                  validationErrors={validationErrors}
                />
              </>
            )}

            {formData.industry === 'ecommerce' && (
              <>
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.conversionRateLabel')}
                  value={formData.ecommerceConversionRate ?? ''}
                  onChange={(e) => onNumericChange('ecommerceConversionRate', e.target.value)}
                  placeholder="Ej: 2.5"
                  fieldName="ecommerceConversionRate"
                  tooltip={tooltips.ecommerceConversionRate}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.returnRateLabel')}
                  value={formData.returnRate ?? ''}
                  onChange={(e) => onNumericChange('returnRate', e.target.value)}
                  placeholder="Ej: 15"
                  fieldName="returnRate"
                  tooltip={tooltips.returnRate}
                  validationErrors={validationErrors}
                />
                
                <div className="col-span-1 md:col-span-2">
                  <div className="bg-gradient-to-r from-blue-600/10 to-green-600/10 rounded-lg p-4 border border-blue-500/20">
                    <h5 className="text-lg font-bold text-white mb-3 flex items-center">
                      <span className="text-xl mr-2">🛒</span>
                      {t('home.calculator.fields.salesChannels')}
                    </h5>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ValidatedInput
                        label={t('home.calculator.fields.marketplacesLabel')}
                        value={formData.sellsOnMarketplaces ?? ''}
                        onChange={(e) => onSelectChange('sellsOnMarketplaces', e.target.value)}
                        placeholder={t('home.calculator.fields.selectOption')}
                        options={[
                          { value: 'yes', label: t('home.calculator.fields.yesMarketplaces') },
                          { value: 'no', label: t('home.calculator.fields.noMarketplaces') },
                          { value: 'both', label: t('home.calculator.fields.bothChannels') }
                        ]}
                        fieldName="sellsOnMarketplaces"
                        tooltip={tooltips.sellsOnMarketplaces}
                        validationErrors={validationErrors}
                      />
                      
                      {(formData.sellsOnMarketplaces === 'yes' || formData.sellsOnMarketplaces === 'both') && (
                        <ValidatedInput
                          type="number"
                          label="Número de Marketplaces"
                          value={formData.numMarketplaces ?? ''}
                          onChange={(e) => onNumericChange('numMarketplaces', e.target.value)}
                          placeholder="Ej: 3"
                          fieldName="numMarketplaces"
                          tooltip={tooltips.numMarketplaces}
                          validationErrors={validationErrors}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {formData.industry === 'consulting' && (
              <ValidatedInput
                label="Certificaciones Principales"
                value={formData.certifications ?? ''}
                onChange={(e) => onTextChange('certifications', e.target.value)}
                placeholder="Ej: PMP, Six Sigma, MBA"
                fieldName="certifications"
                tooltip={tooltips.certifications}
                validationErrors={validationErrors}
              />
            )}

            {formData.industry === 'healthcare' && (
              <>
                <ValidatedInput
                  label="Especializaciones Médicas"
                  value={formData.specializations ?? ''}
                  onChange={(e) => onTextChange('specializations', e.target.value)}
                  placeholder="Ej: Cardiología, Neurología"
                  fieldName="specializations"
                  tooltip={tooltips.specializations}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  label="Licencias Médicas"
                  value={formData.medicalLicenses ?? ''}
                  onChange={(e) => onTextChange('medicalLicenses', e.target.value)}
                  placeholder="Ej: Licencia médica estatal"
                  fieldName="medicalLicenses"
                  tooltip={tooltips.medicalLicenses}
                  validationErrors={validationErrors}
                />
              </>
            )}

            {formData.industry === 'education' && (
              <ValidatedInput
                label="Acreditaciones"
                value={formData.accreditations ?? ''}
                onChange={(e) => onTextChange('accreditations', e.target.value)}
                placeholder="Ej: ISO 9001, NAEYC"
                fieldName="accreditations"
                tooltip={tooltips.accreditations}
                validationErrors={validationErrors}
              />
            )}

            {formData.industry === 'finance' && (
              <>
                <ValidatedInput
                  label="Productos Financieros"
                  value={formData.financialProducts ?? ''}
                  onChange={(e) => onTextChange('financialProducts', e.target.value)}
                  placeholder="Ej: Préstamos, Seguros, Inversiones"
                  fieldName="financialProducts"
                  tooltip={tooltips.financialProducts}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  label="Certificaciones Regulatorias"
                  value={formData.compliance ?? ''}
                  onChange={(e) => onTextChange('compliance', e.target.value)}
                  placeholder="Ej: CNMV, Banco de España"
                  fieldName="compliance"
                  tooltip={tooltips.compliance}
                  validationErrors={validationErrors}
                />
              </>
            )}

            {formData.industry === 'manufacturing' && (
              <>
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.manufacturingCapacity')}
                  value={formData.manufacturingCapacity ?? ''}
                  onChange={(e) => onNumericChange('manufacturingCapacity', e.target.value)}
                  placeholder={t('home.calculator.fields.manufacturingCapacityPlaceholder')}
                  fieldName="manufacturingCapacity"
                  tooltip={tooltips.manufacturingCapacity}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  label={t('home.calculator.fields.qualityCertifications')}
                  value={formData.qualityCertifications ?? ''}
                  onChange={(e) => onTextChange('qualityCertifications', e.target.value)}
                  placeholder={t('home.calculator.fields.qualityCertificationsPlaceholder')}
                  fieldName="qualityCertifications"
                  tooltip={tooltips.qualityCertifications}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.automationLevel')}
                  value={formData.automationLevel ?? ''}
                  onChange={(e) => onNumericChange('automationLevel', e.target.value)}
                  placeholder={t('home.calculator.fields.automationLevelPlaceholder')}
                  fieldName="automationLevel"
                  tooltip={tooltips.automationLevel}
                  validationErrors={validationErrors}
                />
              </>
            )}

            {formData.industry === 'real_estate' && (
              <>
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.propertiesManaged')}
                  value={formData.propertiesManaged ?? ''}
                  onChange={(e) => onNumericChange('propertiesManaged', e.target.value)}
                  placeholder={t('home.calculator.fields.propertiesManagedPlaceholder')}
                  fieldName="propertiesManaged"
                  tooltip={tooltips.propertiesManaged}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.portfolioValue')}
                  value={formData.portfolioValue ?? ''}
                  onChange={(e) => onNumericChange('portfolioValue', e.target.value)}
                  placeholder={t('home.calculator.fields.portfolioValuePlaceholder')}
                  fieldName="portfolioValue"
                  tooltip={tooltips.portfolioValue}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.occupancyRate')}
                  value={formData.occupancyRate ?? ''}
                  onChange={(e) => onNumericChange('occupancyRate', e.target.value)}
                  placeholder={t('home.calculator.fields.occupancyRatePlaceholder')}
                  fieldName="occupancyRate"
                  tooltip={tooltips.occupancyRate}
                  validationErrors={validationErrors}
                />
              </>
            )}

            {formData.industry === 'hospitality' && (
              <>
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.roomsAvailable')}
                  value={formData.roomsAvailable ?? ''}
                  onChange={(e) => onNumericChange('roomsAvailable', e.target.value)}
                  placeholder={t('home.calculator.fields.roomsAvailablePlaceholder')}
                  fieldName="roomsAvailable"
                  tooltip={tooltips.roomsAvailable}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  step="0.1"
                  label={t('home.calculator.fields.averageRating')}
                  value={formData.averageRating ?? ''}
                  onChange={(e) => onNumericChange('averageRating', e.target.value)}
                  placeholder={t('home.calculator.fields.averageRatingPlaceholder')}
                  fieldName="averageRating"
                  tooltip={tooltips.averageRating}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.seasonalRevenue')}
                  value={formData.seasonalRevenue ?? ''}
                  onChange={(e) => onNumericChange('seasonalRevenue', e.target.value)}
                  placeholder={t('home.calculator.fields.seasonalRevenuePlaceholder')}
                  fieldName="seasonalRevenue"
                  tooltip={tooltips.seasonalRevenue}
                  validationErrors={validationErrors}
                />
              </>
            )}

            {formData.industry === 'marketing' && (
              <>
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.campaignsManaged')}
                  value={formData.campaignsManaged ?? ''}
                  onChange={(e) => onNumericChange('campaignsManaged', e.target.value)}
                  placeholder={t('home.calculator.fields.campaignsManagedPlaceholder')}
                  fieldName="campaignsManaged"
                  tooltip={tooltips.campaignsManaged}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.adSpendMonthly')}
                  value={formData.adSpendMonthly ?? ''}
                  onChange={(e) => onNumericChange('adSpendMonthly', e.target.value)}
                  placeholder={t('home.calculator.fields.adSpendMonthlyPlaceholder')}
                  fieldName="adSpendMonthly"
                  tooltip={tooltips.adSpendMonthly}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  step="0.1"
                  label={t('home.calculator.fields.averageROAS')}
                  value={formData.averageROAS ?? ''}
                  onChange={(e) => onNumericChange('averageROAS', e.target.value)}
                  placeholder={t('home.calculator.fields.averageROASPlaceholder')}
                  fieldName="averageROAS"
                  tooltip={tooltips.averageROAS}
                  validationErrors={validationErrors}
                />
                
                <ValidatedInput
                  type="number"
                  label={t('home.calculator.fields.clientsGrowth')}
                  value={formData.clientsGrowth ?? ''}
                  onChange={(e) => onNumericChange('clientsGrowth', e.target.value)}
                  placeholder={t('home.calculator.fields.clientsGrowthPlaceholder')}
                  fieldName="clientsGrowth"
                  tooltip={tooltips.clientsGrowth}
                  validationErrors={validationErrors}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Campos específicos para marca personal */}
      {brandType === 'personal' && (
        <div className="bg-gradient-to-r from-green-600/10 to-teal-600/10 rounded-2xl p-6 border border-green-500/20">
          <h4 className="text-xl font-bold text-white mb-4 flex items-center">
            <span className="text-2xl mr-3">👤</span>
            {t('home.calculator.sections.personalBrandMetrics')}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ValidatedInput
              type="number"
              label={t('home.calculator.fields.projects')}
              value={formData.projects ?? ''}
              onChange={(e) => onTextChange('projects', e.target.value)}
              placeholder="Ej: 25"
              fieldName="projects"
              tooltip={tooltips.projects}
              validationErrors={validationErrors}
            />
            
            <ValidatedInput
              type="number"
              label={t('home.calculator.fields.hourlyRate')}
              value={formData.hourlyRate ?? ''}
              onChange={(e) => onTextChange('hourlyRate', e.target.value)}
              placeholder="Ej: 75"
              fieldName="hourlyRate"
              tooltip={tooltips.hourlyRate}
              validationErrors={validationErrors}
            />
            
            <ValidatedInput
              type="number"
              label={t('home.calculator.fields.coursesCreated')}
              value={formData.coursesCreated ?? ''}
              onChange={(e) => onTextChange('coursesCreated', e.target.value)}
              placeholder="Ej: 3"
              fieldName="coursesCreated"
              tooltip={tooltips.coursesCreated}
              validationErrors={validationErrors}
            />
            
            <ValidatedInput
              type="number"
              label={t('home.calculator.fields.talksGiven')}
              value={formData.talksGiven ?? ''}
              onChange={(e) => onTextChange('talksGiven', e.target.value)}
              placeholder="Ej: 12"
              fieldName="talksGiven"
              tooltip={tooltips.talksGiven}
              validationErrors={validationErrors}
            />
            
            <ValidatedInput
              type="number"
              label={t('home.calculator.fields.collaborations')}
              value={formData.collaborations ?? ''}
              onChange={(e) => onTextChange('collaborations', e.target.value)}
              placeholder="Ej: 8"
              fieldName="collaborations"
              tooltip={tooltips.collaborations}
              validationErrors={validationErrors}
            />
            
            <ValidatedInput
              type="number"
              label={t('home.calculator.fields.contentPublished')}
              value={formData.contentPublished ?? ''}
              onChange={(e) => onTextChange('contentPublished', e.target.value)}
              placeholder="Ej: 150"
              fieldName="contentPublished"
              tooltip={tooltips.contentPublished}
              validationErrors={validationErrors}
            />
            
            <ValidatedInput
              type="number"
              label={t('home.calculator.fields.emailListSize')}
              value={formData.emailListSize ?? ''}
              onChange={(e) => onTextChange('emailListSize', e.target.value)}
              placeholder="Ej: 2500"
              fieldName="emailListSize"
              tooltip={tooltips.emailListSize}
              validationErrors={validationErrors}
            />
          </div>
        </div>
      )}
    </div>
  );
});

// Step3Component moved outside - NO HOOKS ALLOWED
const Step3Component = React.memo(function Step3Component({
  brandType,
  formData,
  handleNumericInput,
  handleSelectChange,
  tooltips,
  validationErrors,
  t
}) {
  // Recognition options with translation
  const recognitionOptions = [
    { value: 'local', label: t('home.calculator.options.recognition.local') },
    { value: 'national', label: t('home.calculator.options.recognition.national') },
    { value: 'international', label: t('home.calculator.options.recognition.international') },
    { value: 'niche', label: t('home.calculator.options.recognition.niche') }
  ];

  // Position options with translation
  const positionOptions = [
    { value: 'leader', label: t('home.calculator.options.marketPosition.marketLeader') },
    { value: 'established', label: t('home.calculator.options.marketPosition.established') },
    { value: 'emerging', label: t('home.calculator.options.marketPosition.emerging') },
    { value: 'niche', label: t('home.calculator.options.marketPosition.nicheSpecialist') }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-white mb-2">
          📊 {t('home.calculator.sections.resourcesPosition')}
        </h3>
        <p className="text-gray-300">
          {t('home.calculator.subtitles.resourcesPosition')}
        </p>
      </div>

      <ValidatedInput
        type="number"
        label={brandType === 'company' ? t('home.calculator.fields.tangibleAssets') : t('home.calculator.fields.personalTangibleAssets')}
        value={formData.tangibleAssets}
        onChange={(e) => handleNumericInput(e, 'tangibleAssets')}
        placeholder="Ej: 50000"
        fieldName="tangibleAssets"
        tooltip={tooltips.tangibleAssets}
        validationErrors={validationErrors}
        t={t}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ValidatedInput
          label={brandType === 'company' ? t('home.calculator.fields.brandRecognition') : t('home.calculator.fields.personalBrandRecognition')}
          value={formData.brandRecognition}
          onChange={handleSelectChange('brandRecognition')}
          placeholder={t('home.calculator.placeholders.selectRecognition')}
          options={recognitionOptions}
          fieldName="brandRecognition"
          tooltip={tooltips.brandRecognition}
          validationErrors={validationErrors}
          t={t}
        />
        
        <ValidatedInput
          label={brandType === 'company' ? t('home.calculator.fields.marketPosition') : t('home.calculator.fields.personalMarketPosition')}
          value={formData.marketPosition}
          onChange={handleSelectChange('marketPosition')}
          placeholder={t('home.calculator.placeholders.selectPosition')}
          options={positionOptions}
          fieldName="marketPosition"
          tooltip={tooltips.marketPosition}
          validationErrors={validationErrors}
          t={t}
        />
      </div>
    </div>
  );
});

// Step4Component moved outside - NO HOOKS ALLOWED  
const Step4Component = React.memo(function Step4Component({
  formData,
  handleTextInput,
  tooltips,
  validationErrors,
  t
}) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-white mb-2">
          😊 {t('home.calculator.sections.sentimentAnalysis')}
        </h3>
        <p className="text-gray-300">
          {t('home.calculator.subtitles.sentimentAnalysis')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <h4 className="text-xl font-bold text-white mb-6 flex items-center">
            <span className="text-2xl mr-3">🌐</span>
            {t('home.calculator.blocks.socialMedia')}
          </h4>
          
          <div className="space-y-6">
            <ValidatedInput
              label={t('home.calculator.fields.twitterProfile')}
              value={formData.twitterProfile}
              onChange={handleTextInput('twitterProfile')}
              placeholder="https://twitter.com/tu-perfil"
              fieldName="twitterProfile"
              tooltip={tooltips.twitterProfile}
              validationErrors={validationErrors}
              t={t}
            />
            
            <ValidatedInput
              label={t('home.calculator.fields.instagramProfile')}
              value={formData.instagramProfile}
              onChange={handleTextInput('instagramProfile')}
              placeholder="https://instagram.com/tu-perfil"
              fieldName="instagramProfile"
              tooltip={tooltips.instagramProfile}
              validationErrors={validationErrors}
              t={t}
            />
            
            <ValidatedInput
              label={t('home.calculator.fields.tiktokProfile')}
              value={formData.tiktokProfile}
              onChange={handleTextInput('tiktokProfile')}
              placeholder="https://www.tiktok.com/@tu-usuario"
              fieldName="tiktokProfile"
              tooltip={tooltips.tiktokProfile}
              validationErrors={validationErrors}
              t={t}
            />
            
            <ValidatedInput
              label={t('home.calculator.fields.linkedinProfile')}
              value={formData.linkedinProfile}
              onChange={handleTextInput('linkedinProfile')}
              placeholder="https://linkedin.com/in/tu-perfil"
              fieldName="linkedinProfile"
              tooltip={tooltips.linkedinProfile}
              validationErrors={validationErrors}
              t={t}
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <h4 className="text-xl font-bold text-white mb-6 flex items-center">
            <span className="text-2xl mr-3">#️⃣</span>
            {t('home.calculator.blocks.hashtagsMentions')}
          </h4>
          
          <div className="space-y-6">
            <ValidatedInput
              label={t('home.calculator.fields.mainHashtags')}
              value={formData.hashtags}
              onChange={handleTextInput('hashtags')}
              placeholder="#tecnologia #innovacion #empresa"
              fieldName="hashtags"
              tooltip={tooltips.hashtags}
              validationErrors={validationErrors}
              t={t}
            />
            
            <div className="text-sm text-gray-400 bg-slate-700/30 p-4 rounded-lg">
              <h5 className="font-medium text-gray-300 mb-2">📋 {t('home.calculator.consent.label')}</h5>
              <label className="flex items-start space-x-3">
                <input 
                  type="checkbox" 
                  checked={formData.consentimiento || false}
                  onChange={(e) => handleTextInput('consentimiento')({ target: { value: e.target.checked } })}
                  className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="text-xs leading-relaxed">
                  {t('home.calculator.consent.text')}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Step2Component moved outside to prevent re-creation on renders
const Step2Component = React.memo(function Step2Component({ 
  brandType, 
  formData, 
  handleNumericInput,
  handleTextInput, 
  handleSelectChange,
  tooltips,
  t 
}) {
  // Recognition options with translation
  const recognitionOptions = [
    { value: 'local', label: t('home.calculator.options.recognition.local') },
    { value: 'national', label: t('home.calculator.options.recognition.national') },
    { value: 'international', label: t('home.calculator.options.recognition.international') },
    { value: 'niche', label: t('home.calculator.options.recognition.niche') }
  ];

  // Position options with translation - also needed in Step2
  const positionOptions = [
    { value: 'leader', label: t('home.calculator.options.marketPosition.marketLeader') },
    { value: 'established', label: t('home.calculator.options.marketPosition.established') },
    { value: 'emerging', label: t('home.calculator.options.marketPosition.emerging') },
    { value: 'niche', label: t('home.calculator.options.marketPosition.nicheSpecialist') }
  ];

  return (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h3 className="text-2xl font-bold text-white mb-2">
        👥 {t('home.calculator.sections.audienceNetworks')}
      </h3>
      <p className="text-gray-300">
        {t('home.calculator.subtitles.audienceInfo')}
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <ValidatedInput
        type="number"
        label={brandType === 'company' ? t('home.calculator.fields.clients') : t('home.calculator.fields.personalClients')}
        value={formData.clients}
        onChange={(e) => handleNumericInput(e, 'clients')}
        placeholder="Ej: 150"
        fieldName="clients"
        tooltip={tooltips.clients}
        t={t}
      />

      <ValidatedInput
        type="number"
        label={brandType === 'company' ? t('home.calculator.fields.yearsInMarket') : t('home.calculator.fields.personalYearsInMarket')}
        value={formData.yearsInMarket}
        onChange={(e) => handleNumericInput(e, 'yearsInMarket')}
        placeholder="Ej: 5"
        fieldName="yearsInMarket"
        tooltip={tooltips.yearsInMarket}
        t={t}
      />

      <ValidatedInput
        type="number"
        label={t('home.calculator.fields.socialFollowers')}
        value={formData.socialFollowers}
        onChange={(e) => handleNumericInput(e, 'socialFollowers')}
        placeholder="Ej: 10000"
        fieldName="socialFollowers"
        tooltip={tooltips.socialFollowers}
        t={t}
      />

      <ValidatedInput
        type="number"
        label={t('home.calculator.fields.monthlyTraffic')}
        value={formData.monthlyTraffic}
        onChange={(e) => handleNumericInput(e, 'monthlyTraffic')}
        placeholder="Ej: 5000"
        fieldName="monthlyTraffic"
        tooltip={tooltips.monthlyTraffic}
        t={t}
      />


    </div>

    <div className="text-center">
      <p className="text-gray-400 text-sm">
        💡 <strong>Tip:</strong> {t('home.calculator.tips.audienceImpact')}
      </p>
    </div>
  </div>
);
});

const ImprovedCalculator = () => {
  const { t, language } = useTranslations();
  const [currentStep, setCurrentStep] = useState('lead'); // 'lead', 'brandType', 'step1', 'step2', 'step3', 'step4', 'results'
  const [leadData, setLeadData] = useState({
    name: '',
    email: ''
  });

  // Format money for sharing with proper locale
  const formatMoney = (value, locale = language, currency = "EUR") => {
    return new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-GB', { 
      style: "currency", 
      currency 
    }).format(value);
  };

  // NUEVA FUNCIÓN: Generador de recomendaciones personalizadas avanzadas (INTERNACIONALIZADA)
  const generatePersonalizedRecommendations = (formData, results, brandType, t) => {
    const recommendations = [];
    const totalValue = results?.totalValue || 0;
    const breakdown = results?.breakdown || {};
    
    // Extraer datos del usuario
    const clients = parseFloat(formData.clients) || 0;
    const yearsInMarket = parseFloat(formData.yearsInMarket) || 0;
    const followers = parseFloat(formData.socialFollowers) || 0;
    const revenue = parseFloat(brandType === 'company' ? formData.revenue : formData.personalRevenue) || 0;
    const industry = brandType === 'company' ? formData.industry : formData.personalNiche;
    const monthlyTraffic = parseFloat(formData.monthlyTraffic) || 0;
    
    // Calcular métricas derivadas
    const revenuePerClient = clients > 0 ? Math.round(revenue / clients) : 0;
    const clientsPerYear = yearsInMarket > 0 ? Math.round(clients / yearsInMarket) : clients;
    const engagementRate = followers > 0 && monthlyTraffic > 0 ? Math.round((monthlyTraffic / followers) * 100) : 0;
    
    // FASE 1: Lógica condicional por tipo de marca
    if (brandType === 'personal') {
      // RECOMENDACIONES PARA MARCA PERSONAL
      
      // Recomendación basada en seguidores vs conversión
      if (followers > 10000 && revenuePerClient < 1000) {
        recommendations.push({
          icon: '💰',
          title: t('personalizedRecommendations.personalBrand.monetizeReach.title'),
          text: t('personalizedRecommendations.personalBrand.monetizeReach.text', { 
            followers: followers.toLocaleString(), 
            revenuePerClient: revenuePerClient 
          }),
          priority: 'high',
          category: 'monetization'
        });
      }
      
      // Recomendación basada en años de experiencia
      if (yearsInMarket < 2) {
        const textKey = yearsInMarket === 1 ? 
          'personalizedRecommendations.personalBrand.buildAuthority.textOneYear' :
          'personalizedRecommendations.personalBrand.buildAuthority.text';
        
        recommendations.push({
          icon: '🌱',
          title: t('personalizedRecommendations.personalBrand.buildAuthority.title'),
          text: yearsInMarket === 1 ? 
            t(textKey) : 
            t(textKey, { years: yearsInMarket + (language === 'es' ? ' años' : ' years') }),
          priority: 'high',
          category: 'authority'
        });
      } else if (yearsInMarket >= 5) {
        recommendations.push({
          icon: '🏆',
          title: t('personalizedRecommendations.personalBrand.diversifyIncome.title'),
          text: t('personalizedRecommendations.personalBrand.diversifyIncome.text', { years: yearsInMarket }),
          priority: 'medium',
          category: 'diversification'
        });
      }
      
      // Recomendación basada en base de clientes
      if (clients < 50) {
        recommendations.push({
          icon: '🎯',
          title: t('personalizedRecommendations.personalBrand.expandClientBase.title'),
          text: t('personalizedRecommendations.personalBrand.expandClientBase.text', { clients: clients }),
          priority: 'high',
          category: 'growth'
        });
      }
      
    } else {
      // RECOMENDACIONES PARA MARCA EMPRESA
      
      // Recomendación basada en número de clientes vs facturación
      if (clients > 0 && revenuePerClient < 500) {
        recommendations.push({
          icon: '📈',
          title: t('personalizedRecommendations.companyBrand.optimizeClientValue.title'),
          text: t('personalizedRecommendations.companyBrand.optimizeClientValue.text', { 
            clients: clients, 
            revenuePerClient: revenuePerClient 
          }),
          priority: 'high',
          category: 'optimization'
        });
      }
      
      // Recomendación basada en crecimiento
      if (yearsInMarket >= 3 && clientsPerYear < 50) {
        recommendations.push({
          icon: '🚀',
          title: t('personalizedRecommendations.companyBrand.accelerateGrowth.title'),
          text: t('personalizedRecommendations.companyBrand.accelerateGrowth.text', { 
            years: yearsInMarket, 
            clientsPerYear: clientsPerYear 
          }),
          priority: 'high',
          category: 'growth'
        });
      }
      
      // Recomendación basada en presencia digital
      if (followers < 5000 && revenue > 100000) {
        recommendations.push({
          icon: '📱',
          title: t('personalizedRecommendations.companyBrand.strengthenDigital.title'),
          text: t('personalizedRecommendations.companyBrand.strengthenDigital.text', { 
            revenue: revenue.toLocaleString(), 
            followers: followers 
          }),
          priority: 'medium',
          category: 'digital'
        });
      }
    }
    
    // FASE 2: Adaptación por sector específico
    const sectorRecommendations = generateSectorSpecificRecommendations(industry, formData, brandType, t);
    recommendations.push(...sectorRecommendations);
    
    // FASE 3: Métricas cuantitativas adicionales
    
    // Recomendación general por valor total
    if (totalValue < 50000) {
      recommendations.push({
        icon: '⚡',
        title: t('personalizedRecommendations.general.accelerateGrowth.title'),
        text: t('personalizedRecommendations.general.accelerateGrowth.text', { 
          totalValue: totalValue.toLocaleString() 
        }),
        priority: 'high',
        category: 'foundation'
      });
    } else if (totalValue > 500000) {
      recommendations.push({
        icon: '🎯',
        title: t('personalizedRecommendations.general.highValueStrategy.title'),
        text: t('personalizedRecommendations.general.highValueStrategy.text', { 
          totalValue: totalValue.toLocaleString() 
        }),
        priority: 'medium',
        category: 'scaling'
      });
    }
    
    // Limitar a las 3 recomendaciones más relevantes
    return recommendations
      .sort((a, b) => {
        const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      })
      .slice(0, 3);
  };

  // NUEVA FUNCIÓN: Recomendaciones específicas por sector (INTERNACIONALIZADA)
  const generateSectorSpecificRecommendations = (sector, formData, brandType, t) => {
    const recommendations = [];
    const clients = parseFloat(formData.clients) || 0;
    const revenue = parseFloat(brandType === 'company' ? formData.revenue : formData.personalRevenue) || 0;
    const followers = parseFloat(formData.socialFollowers) || 0;
    
    switch (sector) {
      case 'hospitality':
        if (clients < 100) {
          recommendations.push({
            icon: '🍽️',
            title: t('personalizedRecommendations.sectors.hospitality.title'),
            text: t('personalizedRecommendations.sectors.hospitality.text', { clients: clients }),
            priority: 'high',
            category: 'sector'
          });
        }
        break;
        
      case 'ecommerce':
        const conversionRate = followers > 0 ? Math.round((clients / followers) * 100) : 0;
        if (conversionRate < 2) {
          recommendations.push({
            icon: '🛒',
            title: t('personalizedRecommendations.sectors.ecommerce.title'),
            text: t('personalizedRecommendations.sectors.ecommerce.text', { conversionRate: conversionRate }),
            priority: 'high',
            category: 'sector'
          });
        }
        break;
        
      case 'technology':
        if (revenue > 100000 && clients < 200) {
          recommendations.push({
            icon: '⚙️',
            title: t('personalizedRecommendations.sectors.technology.title'),
            text: t('personalizedRecommendations.sectors.technology.text', { 
              revenue: revenue.toLocaleString(), 
              clients: clients 
            }),
            priority: 'high',
            category: 'sector'
          });
        }
        break;
        
      case 'education':
        if (brandType === 'personal' && followers > 5000) {
          recommendations.push({
            icon: '📚',
            title: t('personalizedRecommendations.sectors.education.title'),
            text: t('personalizedRecommendations.sectors.education.text', { 
              followers: followers.toLocaleString() 
            }),
            priority: 'high',
            category: 'sector'
          });
        }
        break;
        
      case 'consulting':
        const hourlyRate = clients > 0 ? Math.round(revenue / (clients * 100)) : 0; // Estimación
        if (hourlyRate < 100) {
          recommendations.push({
            icon: '💼',
            title: t('personalizedRecommendations.sectors.consulting.title'),
            text: t('personalizedRecommendations.sectors.consulting.text'),
            priority: 'high',
            category: 'sector'
          });
        }
        break;
        
      case 'finance':
        if (clients > 500 && revenue < 200000) {
          recommendations.push({
            icon: '💰',
            title: t('personalizedRecommendations.sectors.finance.title'),
            text: t('personalizedRecommendations.sectors.finance.text', { 
              clients: clients, 
              revenue: revenue.toLocaleString() 
            }),
            priority: 'medium',
            category: 'sector'
          });
        }
        break;
        
      case 'healthcare':
        if (brandType === 'personal' && clients < 100) {
          recommendations.push({
            icon: '🏥',
            title: t('personalizedRecommendations.sectors.healthcare.title'),
            text: t('personalizedRecommendations.sectors.healthcare.text', { clients: clients }),
            priority: 'medium',
            category: 'sector'
          });
        }
        break;
        
      default:
        break;
    }
    
    return recommendations;
  };

  // Componente de recomendaciones personalizadas mejorado

  // NUEVA FUNCIÓN: Generador de insights personalizados para toda la página de resultados
  const generatePersonalizedInsights = (formData, results, brandType, language = 'es') => {
    const insights = {
      heroMessage: '',
      projectionInsights: {
        conservative: { title: '', bullets: [] },
        optimistic: { title: '', bullets: [] },
        aggressive: { title: '', bullets: [] }
      },
      keyFactors: [],
      competitiveAnalysis: '',
      nextSteps: []
    };

    // Extraer datos del usuario
    const totalValue = results?.totalValue || 0;
    const clients = parseFloat(formData.clients) || 0;
    const yearsInMarket = parseFloat(formData.yearsInMarket) || 0;
    const followers = parseFloat(formData.socialFollowers) || 0;
    const revenue = parseFloat(brandType === 'company' ? formData.revenue : formData.personalRevenue) || 0;
    const industry = brandType === 'company' ? formData.industry : formData.personalNiche;
    const businessModel = formData.businessModel;
    const recurrentShare = parseFloat(formData.recurrentShare) || 0;
    const monthlyTraffic = parseFloat(formData.monthlyTraffic) || 0;

    // Calcular métricas derivadas
    const revenuePerClient = clients > 0 ? Math.round(revenue / clients) : 0;
    const engagementRate = followers > 0 && monthlyTraffic > 0 ? Math.round((monthlyTraffic / followers) * 100) : 0;
    const isRecurrentModel = recurrentShare > 50;
    const hasStrongDigital = followers > 5000 || monthlyTraffic > 1000;

    if (language === 'es') {
      // MENSAJE HERO PERSONALIZADO
      if (brandType === 'personal') {
        if (totalValue > 300000) {
          insights.heroMessage = `Como marca personal en ${industry} con una valoración de ${totalValue.toLocaleString()}€, estás en el top 10% de profesionales de tu sector. Con ${clients} clientes y ${followers.toLocaleString()} seguidores, tu marca tiene un potencial excepcional para monetización premium.`;
        } else if (totalValue > 100000) {
          insights.heroMessage = `Tu marca personal en ${industry} muestra un crecimiento sólido con ${totalValue.toLocaleString()}€ de valoración. Con ${yearsInMarket} años de experiencia y ${clients} clientes, estás en posición ideal para acelerar tu crecimiento.`;
        } else {
          insights.heroMessage = `Como marca personal emergente en ${industry}, tu valoración de ${totalValue.toLocaleString()}€ refleja bases sólidas. Con ${clients} clientes actuales, tienes amplias oportunidades de crecimiento por explorar.`;
        }
      } else {
        if (isRecurrentModel) {
          insights.heroMessage = `Tu empresa en ${industry} con ${recurrentShare}% de ingresos recurrentes muestra un modelo de negocio robusto. Con ${clients} clientes y ${totalValue.toLocaleString()}€ de valoración, estás en posición privilegiada para escalar.`;
        } else if (hasStrongDigital) {
          insights.heroMessage = `Tu empresa en ${industry} con fuerte presencia digital (${followers.toLocaleString()} seguidores) y valoración de ${totalValue.toLocaleString()}€ tiene excelente posicionamiento para expansion multicanal.`;
        } else {
          insights.heroMessage = `Como empresa establecida en ${industry} con ${clients} clientes y ${yearsInMarket} años en el mercado, tu valoración de ${totalValue.toLocaleString()}€ refleja estabilidad y potencial de optimización.`;
        }
      }

      // PROYECCIONES PERSONALIZADAS
      if (brandType === 'personal') {
        insights.projectionInsights.conservative = {
          title: 'Crecimiento Orgánico',
          bullets: [
            `Ampliar base de clientes de ${clients} a ${Math.round(clients * 1.2)} mediante networking`,
            `Incrementar tarifa promedio basándose en tu experiencia de ${yearsInMarket} años`
          ]
        };
        insights.projectionInsights.optimistic = {
          title: 'Expansión Estratégica',
          bullets: [
            `Monetizar audiencia de ${followers.toLocaleString()} seguidores con productos digitales`,
            `Diversificar servicios para maximizar revenue per client (actual: ${revenuePerClient}€)`
          ]
        };
        insights.projectionInsights.aggressive = {
          title: 'Escalabilidad Premium',
          bullets: [
            `Lanzar programa de mentoring/formación para escalar sin límite temporal`,
            `Establecer partnerships estratégicos para multiplicar alcance`
          ]
        };
      } else {
        if (isRecurrentModel) {
          insights.projectionInsights.conservative = {
            title: 'Consolidación SaaS',
            bullets: [
              `Optimizar retención de ${clients} clientes actuales con ${recurrentShare}% recurrencia`,
              `Incrementar ARPU mediante features premium`
            ]
          };
        } else {
          insights.projectionInsights.conservative = {
            title: 'Optimización Operacional',
            bullets: [
              `Aumentar eficiencia en captación (actual: ${Math.round(clients/yearsInMarket)} clientes/año)`,
              `Implementar sistemas de fidelización para mejorar LTV`
            ]
          };
        }
        
        insights.projectionInsights.optimistic = {
          title: 'Expansión de Mercado',
          bullets: [
            hasStrongDigital ? 
              `Aprovechar presencia digital (${followers.toLocaleString()} seguidores) para nuevos canales` :
              `Desarrollar presencia digital para ampliar reach`,
            `Expandir oferta de servicios en ${industry} con alto margen`
          ]
        };
        
        insights.projectionInsights.aggressive = {
          title: 'Disrupción Sectorial',
          bullets: [
            `Liderar innovación en ${industry} mediante tecnología`,
            `Preparar estrategia de salida o ronda de inversión`
          ]
        };
      }

      // FACTORES CLAVE PERSONALIZADOS
      if (engagementRate > 5) {
        insights.keyFactors.push(`📱 Tu engagement rate del ${engagementRate}% supera el promedio del sector`);
      }
      if (revenuePerClient > 1000) {
        insights.keyFactors.push(`💰 Alto valor por cliente (${revenuePerClient}€) indica premium positioning`);
      }
      if (isRecurrentModel) {
        insights.keyFactors.push(`🔄 Modelo recurrente (${recurrentShare}%) proporciona predictibilidad de ingresos`);
      }
      if (yearsInMarket < 2) {
        insights.keyFactors.push(`🌱 Tu juventud en el mercado (${yearsInMarket} años) es ventaja para adaptabilidad`);
      } else if (yearsInMarket >= 5) {
        insights.keyFactors.push(`🏆 Tu experiencia de ${yearsInMarket} años te posiciona como referente sectorial`);
      }

      // ANÁLISIS COMPETITIVO PERSONALIZADO
      if (brandType === 'personal') {
        insights.competitiveAnalysis = `En el mercado de ${industry}, tu combinación de ${clients} clientes y ${followers.toLocaleString()} seguidores te sitúa ${totalValue > 200000 ? 'por encima' : 'en línea con'} la media de marcas personales similares. Tu ventaja diferenciadora radica en ${hasStrongDigital ? 'tu fuerte presencia digital' : 'tu especialización técnica'}.`;
      } else {
        insights.competitiveAnalysis = `Comparado con empresas similares en ${industry}, tu ${isRecurrentModel ? 'modelo recurrente' : 'estructura de ingresos'} te proporciona ${totalValue > 300000 ? 'una posición competitiva superior' : 'bases sólidas para competir'}. El factor crítico para destacar será ${hasStrongDigital ? 'la optimización de tu presencia digital' : 'el desarrollo de canales digitales'}.`;
      }

    } else {
      // ENGLISH VERSION
      if (brandType === 'personal') {
        if (totalValue > 300000) {
          insights.heroMessage = `As a personal brand in ${industry} with a valuation of €${totalValue.toLocaleString()}, you're in the top 10% of professionals in your sector. With ${clients} clients and ${followers.toLocaleString()} followers, your brand has exceptional potential for premium monetization.`;
        } else if (totalValue > 100000) {
          insights.heroMessage = `Your personal brand in ${industry} shows solid growth with a €${totalValue.toLocaleString()} valuation. With ${yearsInMarket} years of experience and ${clients} clients, you're ideally positioned to accelerate your growth.`;
        } else {
          insights.heroMessage = `As an emerging personal brand in ${industry}, your €${totalValue.toLocaleString()} valuation reflects solid foundations. With ${clients} current clients, you have ample growth opportunities to explore.`;
        }
      } else {
        if (isRecurrentModel) {
          insights.heroMessage = `Your company in ${industry} with ${recurrentShare}% recurring revenue shows a robust business model. With ${clients} clients and €${totalValue.toLocaleString()} valuation, you're in a privileged position to scale.`;
        } else if (hasStrongDigital) {
          insights.heroMessage = `Your company in ${industry} with strong digital presence (${followers.toLocaleString()} followers) and €${totalValue.toLocaleString()} valuation has excellent positioning for multichannel expansion.`;
        } else {
          insights.heroMessage = `As an established company in ${industry} with ${clients} clients and ${yearsInMarket} years in the market, your €${totalValue.toLocaleString()} valuation reflects stability and optimization potential.`;
        }
      }

      // ENGLISH PROJECTIONS
      if (brandType === 'personal') {
        insights.projectionInsights.conservative = {
          title: 'Organic Growth',
          bullets: [
            `Expand client base from ${clients} to ${Math.round(clients * 1.2)} through networking`,
            `Increase average rate based on your ${yearsInMarket} years of experience`
          ]
        };
        insights.projectionInsights.optimistic = {
          title: 'Strategic Expansion',
          bullets: [
            `Monetize audience of ${followers.toLocaleString()} followers with digital products`,
            `Diversify services to maximize revenue per client (current: €${revenuePerClient})`
          ]
        };
        insights.projectionInsights.aggressive = {
          title: 'Premium Scalability',
          bullets: [
            `Launch mentoring/training program to scale beyond time constraints`,
            `Establish strategic partnerships to multiply reach`
          ]
        };
      } else {
        if (isRecurrentModel) {
          insights.projectionInsights.conservative = {
            title: 'SaaS Consolidation',
            bullets: [
              `Optimize retention of ${clients} current clients with ${recurrentShare}% recurrence`,
              `Increase ARPU through premium features`
            ]
          };
        } else {
          insights.projectionInsights.conservative = {
            title: 'Operational Optimization',
            bullets: [
              `Increase acquisition efficiency (current: ${Math.round(clients/yearsInMarket)} clients/year)`,
              `Implement loyalty systems to improve LTV`
            ]
          };
        }
        
        insights.projectionInsights.optimistic = {
          title: 'Market Expansion',
          bullets: [
            hasStrongDigital ? 
              `Leverage digital presence (${followers.toLocaleString()} followers) for new channels` :
              `Develop digital presence to expand reach`,
            `Expand service offering in ${industry} with high margins`
          ]
        };
        
        insights.projectionInsights.aggressive = {
          title: 'Sector Disruption',
          bullets: [
            `Lead innovation in ${industry} through technology`,
            `Prepare exit strategy or investment round`
          ]
        };
      }

      // ENGLISH KEY FACTORS
      if (engagementRate > 5) {
        insights.keyFactors.push(`📱 Your ${engagementRate}% engagement rate exceeds industry average`);
      }
      if (revenuePerClient > 1000) {
        insights.keyFactors.push(`💰 High client value (€${revenuePerClient}) indicates premium positioning`);
      }
      if (isRecurrentModel) {
        insights.keyFactors.push(`🔄 Recurring model (${recurrentShare}%) provides revenue predictability`);
      }
      if (yearsInMarket < 2) {
        insights.keyFactors.push(`🌱 Your market youth (${yearsInMarket} years) is an adaptability advantage`);
      } else if (yearsInMarket >= 5) {
        insights.keyFactors.push(`🏆 Your ${yearsInMarket} years of experience position you as a sector reference`);
      }

      // ENGLISH COMPETITIVE ANALYSIS
      if (brandType === 'personal') {
        insights.competitiveAnalysis = `In the ${industry} market, your combination of ${clients} clients and ${followers.toLocaleString()} followers places you ${totalValue > 200000 ? 'above' : 'in line with'} the average of similar personal brands. Your differentiating advantage lies in ${hasStrongDigital ? 'your strong digital presence' : 'your technical specialization'}.`;
      } else {
        insights.competitiveAnalysis = `Compared to similar companies in ${industry}, your ${isRecurrentModel ? 'recurring model' : 'revenue structure'} provides you with ${totalValue > 300000 ? 'a superior competitive position' : 'solid foundations to compete'}. The critical factor to stand out will be ${hasStrongDigital ? 'optimizing your digital presence' : 'developing digital channels'}.`;
      }
    }

    return insights;
  };

  // Auto-scroll to top when component mounts (for navigation from links)
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Si estamos en un estado de results pero sin datos reales, limpiar y reiniciar
    if (currentStep === 'results' && !results) {
      console.log('🧹 Limpiando estado inconsistente al montar componente');
      localStorage.removeItem('calculatorState');
      setCurrentStep('lead');
    }
  }, []);
  const [leadId, setLeadId] = useState(null);
  const [brandType, setBrandType] = useState(''); // 'company' or 'personal'
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false); // Flag to prevent multiple restorations
  
  // 🔍 DIAGNOSTIC LOGGING - Render counter
  const renderCount = React.useRef(0);
  renderCount.current++;
  console.log(`🔄 ImprovedCalculator render #${renderCount.current}`, {
    currentStep,
    brandType,
    timestamp: Date.now()
  });
  
  // Funciones para compartir en redes sociales REALES con URLs canónicas
  const [shareUrl, setShareUrl] = useState('');
  const [isCreatingShareUrl, setIsCreatingShareUrl] = useState(false);
  
  // Estado para el toast de confirmación de copia
  const [showCopyToast, setShowCopyToast] = useState(false);
  
  // Función para mostrar toast de confirmación
  const showCopyConfirmation = () => {
    setShowCopyToast(true);
    setTimeout(() => {
      setShowCopyToast(false);
    }, 2500); // 2.5 segundos como especificado
  };

  const createShareableResult = async () => {
    if (!results || shareUrl) return shareUrl; // Ya existe la URL
    
    setIsCreatingShareUrl(true);
    try {
      const shareData = {
        brand_name: brandType === 'company' ? formData.companyName : formData.personalName,
        brand_type: brandType,
        brand_value: results.totalValue,
        industry: brandType === 'company' ? formData.industry : formData.personalNiche,
        calculation_data: {
          results,
          formData,
          sentimentData
        }
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(shareData),
      });

      if (!response.ok) {
        throw new Error('Error creating shareable result');
      }

      const data = await response.json();
      // Crear URL canónica en formato /r/{resultId}
      const canonicalUrl = `${window.location.origin}/r/${data.result_id}`;
      setShareUrl(canonicalUrl);
      return canonicalUrl;
    } catch (error) {
      console.error('Error creating shareable result:', error);
      // Fallback: usar la URL actual con parámetros
      const fallbackUrl = `${window.location.origin}?v=${Date.now()}&n=${encodeURIComponent(brandType === 'company' ? formData.companyName : formData.personalName)}&val=${results.totalValue}`;
      setShareUrl(fallbackUrl);
      return fallbackUrl;
    } finally {
      setIsCreatingShareUrl(false);
    }
  };

  // Handlers correctos (dentro del onClick, con window.open)
  const shareX = async (e) => {
    e.preventDefault();
    const canonicalUrl = await createShareableResult();
    const value = results.totalValue;
    const amount = formatMoney(value, language);
    
    // Generate localized share text
    const shareText = brandType === 'personal'
      ? t('results.shareModal.card.personal', { value: amount })
      : t('results.shareModal.card.company', { value: amount });
    const shareFooter = t('results.shareModal.card.footer');
    
    const textBase = `🎉 ${shareText}\n📊 ${shareFooter}`;
    const urlX = 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(textBase) +
                 '&url=' + encodeURIComponent(canonicalUrl + '?utm_source=x');
    window.open(urlX, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const shareLinkedIn = async (e) => {
    e.preventDefault();
    const canonicalUrl = await createShareableResult();
    const value = results.totalValue;
    const amount = formatMoney(value, language);
    
    // Generate localized share text
    const shareText = brandType === 'personal'
      ? t('results.shareModal.card.personal', { value: amount })
      : t('results.shareModal.card.company', { value: amount });
    const shareFooter = t('results.shareModal.card.footer');
    
    const lnText = `🎉 ${shareText} — 📊 ${shareFooter}`;
    const urlIn = 'https://www.linkedin.com/sharing/share-offsite/?url=' +
                  encodeURIComponent(canonicalUrl + '?utm_source=linkedin') + 
                  '&summary=' + encodeURIComponent(lnText);
    window.open(urlIn, '_blank', 'noopener,noreferrer,width=800,height=650');
  };

  const shareWhatsApp = async (e) => {
    e.preventDefault();
    const canonicalUrl = await createShareableResult();
    const value = results.totalValue;
    const amount = formatMoney(value, language);
    
    // Generate localized share text
    const shareText = brandType === 'personal'
      ? t('results.shareModal.card.personal', { value: amount })
      : t('results.shareModal.card.company', { value: amount });
    const shareFooter = t('results.shareModal.card.footer');
    
    const textBase = `🎉 ${shareText}\n📊 ${shareFooter}`;
    const msg = `${textBase}\n${canonicalUrl}?utm_source=whatsapp`;
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const base = isMobile ? 'whatsapp://send?text=' : 'https://web.whatsapp.com/send?text=';
    window.open(base + encodeURIComponent(msg), '_blank', 'noopener,noreferrer,width=800,height=650');
  };

  const shareNative = async (e) => {
    e.preventDefault();
    const canonicalUrl = await createShareableResult();
    const value = results.totalValue;
    const amount = formatMoney(value, language);
    const shareUrlWithUTM = canonicalUrl + '?utm_source=webshare';
    
    // Generate localized share text
    const shareText = brandType === 'personal'
      ? t('results.shareModal.card.personal', { value: amount })
      : t('results.shareModal.card.company', { value: amount });
    const shareFooter = t('results.shareModal.card.footer');
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareText,
          text: shareFooter,
          url: shareUrlWithUTM
        });
      } catch (error) {
        // Si el usuario cancela, no hacer nada
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copiar enlace y mostrar toast
      try {
        await navigator.clipboard.writeText(shareUrlWithUTM);
        showCopyConfirmation();
      } catch (error) {
        // Fallback para navegadores antiguos
        const textArea = document.createElement('textarea');
        textArea.value = shareUrlWithUTM;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showCopyConfirmation();
      }
    }
  };

  const [formData, setFormData] = useState({
    // Paso 1: Información básica - Con valores por defecto seguros
    companyName: '',
    personalName: '',
    revenue: '',
    personalRevenue: '',
    industry: '',
    personalNiche: '',
    
    // Nuevos campos opcionales de alto impacto - Paso 1
    businessModel: '',
    revenueModel: '',
    recurrentShare: '',
    customerSegment: '',
    
    // Paso 2: Métricas de audiencia - Con valores por defecto seguros
    clients: '',
    yearsInMarket: '',
    socialFollowers: '',
    monthlyTraffic: '',
    
    // Paso 3: Recursos y reconocimiento - Con valores por defecto seguros
    tangibleAssets: '',
    brandRecognition: '',
    marketPosition: '',
    
    // Paso 4: Análisis de sentimiento - Con valores por defecto seguros
    twitterProfile: '',
    instagramProfile: '',
    tiktokProfile: '',
    facebookProfile: '',
    linkedinProfile: '',
    hashtags: '',
    sentimentConsent: false,
    
    // Campos específicos por sector/industria
    // Tecnología
    activeUsers: '',
    arr: '', // Annual Recurring Revenue
    churnRate: '',
    techStack: '',
    
    // E-commerce
    productsInCatalog: '',
    conversionRate: '',
    averageOrderValue: '',
    returnRate: '',
    sellsOnMarketplaces: '', // yes/no
    numberOfMarketplaces: '', // número de marketplaces
    marketplaceRevenue: '', // % de ingresos de marketplaces
    
    // Consultoría
    completedProjects: '',
    hourlyRate: '',
    certifications: '',
    clientRetention: '',
    
    // Marca Personal específico
    personalProjects: '', // Proyectos realizados
    personalHourlyRate: '', // Tarifa por hora
    personalClients: '', // Clientes activos
    personalCourses: '', // Cursos/productos digitales
    personalSpeaking: '', // Conferencias/charlas por año
    personalCollaborations: '', // Colaboraciones con marcas
    personalContent: '', // Piezas de contenido al mes
    personalEmailList: '', // Suscriptores email
    
    // Salud
    patients: '',
    specializations: '',
    medicalLicenses: '',
    
    // Educación
    students: '',
    courses: '',
    completionRate: '',
    accreditations: '',
    
    // Finanzas
    aum: '', // Assets Under Management
    clientPortfolio: '',
    financialProducts: '',
    compliance: '',
    
    // Marketing
    campaignsManaged: '',
    adSpend: '',
    roas: '', // Return on Ad Spend
    clientsGrowth: ''
  });
  
  const [results, setResults] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isAnalyzingSentiment, setIsAnalyzingSentiment] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);

  // Definir los pasos del formulario (actualizado con sentimiento)
  const formSteps = [
    { id: 'step1', title: t('home.calculator.steps.basicInfo'), icon: '🏢' },
    { id: 'step2', title: t('home.calculator.steps.audienceNetworks'), icon: '👥' },
    { id: 'step3', title: t('home.calculator.steps.resourcesPosition'), icon: '📊' },
    { id: 'step4', title: t('home.calculator.steps.sentimentAnalysis'), icon: '😊' }
  ];

  // Tooltips informativos (actualizados con impacto en valoración)
  const tooltips = {
    revenue: t('home.calculator.fieldTooltips.revenue') || "Ingresos totales anuales de tu empresa antes de gastos. Incluye todas las fuentes de ingreso.",
    personalRevenue: t('home.calculator.fieldTooltips.revenue') || "Ingresos anuales totales generados por tu marca personal (consultorías, cursos, patrocinios, etc.)",
    industry: t('home.calculator.fieldTooltips.industry') || "Sector principal de tu empresa. Esto afecta los multiplicadores de valoración.",
    personalNiche: t('home.calculator.fieldTooltips.industry') || "Tu área de especialización o nicho de mercado como marca personal.",
    clients: t('home.calculator.fieldTooltips.clients') || "Número aproximado de clientes activos o que han comprado en el último año.",
    yearsInMarket: t('home.calculator.fieldTooltips.yearsInMarket') || "Años que llevas operando en el mercado. La longevidad aumenta la valoración.",
    socialFollowers: t('home.calculator.fieldTooltips.followers') || "Suma total de seguidores en todas tus redes sociales principales.",
    monthlyTraffic: t('home.calculator.fieldTooltips.followers') || "Visitantes únicos mensuales aproximados a tu sitio web o plataformas.",
    tangibleAssets: "Valor de activos físicos: equipos, inventario, propiedades, etc.",
    brandRecognition: "Nivel de reconocimiento de tu marca en tu mercado objetivo.",
    marketPosition: "Tu posición competitiva en el mercado o sector.",
    twitterProfile: "URL completa de tu perfil de Twitter/X (ej: https://twitter.com/tunombre)",
    instagramProfile: "URL completa de tu perfil de Instagram (ej: https://instagram.com/tunombre)",
    tiktokProfile: "URL completa de tu perfil de TikTok (ej: https://www.tiktok.com/@tu-usuario)",
    facebookProfile: "URL completa de tu perfil de Facebook (ej: https://facebook.com/tunombre)",
    linkedinProfile: "URL completa de tu perfil de LinkedIn (ej: https://linkedin.com/in/tunombre)",
    hashtags: "Hashtags principales que usas o que están asociados a tu marca (separados por comas)",
    
    // Tooltips específicos por sector - Tecnología
    activeUsers: "Número total de usuarios activos mensuales en tu plataforma o producto",
    arr: "Ingresos recurrentes anuales (ARR) - ingresos por suscripciones que se repiten cada año",
    churnRate: "Porcentaje de clientes que cancelan mensualmente (ej: 5 significa 5% mensual)",
    techStack: "Principales tecnologías que utilizas (ej: React, Python, AWS, etc.)",
    
    // E-commerce
    productsInCatalog: "Número total de productos únicos disponibles en tu tienda",
    conversionRate: "Porcentaje de visitantes que compran (ej: 2.5 significa 2.5%)",
    averageOrderValue: "Valor promedio de cada pedido en euros",
    returnRate: "Porcentaje de productos devueltos (ej: 10 significa 10%)",
    sellsOnMarketplaces: "¿Vendes también a través de marketplaces como Amazon, eBay, etc.?",
    numberOfMarketplaces: "Número de marketplaces diferentes donde vendes (Amazon, eBay, Etsy, etc.)",
    marketplaceRevenue: "Porcentaje de tus ingresos totales que provienen de marketplaces (ej: 40 significa 40%)",
    
    // Consultoría
    completedProjects: "Número total de proyectos completados exitosamente",
    hourlyRate: "Tarifa por hora en euros para tus servicios de consultoría",
    certifications: "Certificaciones profesionales relevantes que posees",
    clientRetention: "Porcentaje de clientes que repiten (ej: 80 significa 80%)",
    
    // Marca Personal específico
    personalProjects: "Proyectos personales destacados realizados en los últimos 2 años",
    personalHourlyRate: "Tarifa por hora para consultorías o servicios personales",
    personalClients: "Clientes activos que trabajan contigo regularmente",
    personalCourses: "Cursos online, ebooks o productos digitales que ofreces",
    personalSpeaking: "Número de conferencias, charlas o webinars por año",
    personalCollaborations: "Colaboraciones con marcas o influencers en el último año",
    personalContent: "Piezas de contenido que publicas mensualmente (posts, videos, artículos)",
    personalEmailList: "Número de suscriptores en tu lista de email/newsletter",
    
    // Salud
    patients: "Número aproximado de pacientes activos o que tratas regularmente",
    specializations: "Áreas médicas de especialización principal",
    medicalLicenses: "Licencias médicas y certificaciones profesionales",
    
    // Educación
    students: "Número total de estudiantes o alumnos activos",
    courses: "Número de cursos, programas o materias que impartes",
    completionRate: "Porcentaje de estudiantes que completan tus cursos exitosamente",
    accreditations: "Acreditaciones educativas y certificaciones institucionales",
    
    // Finanzas
    aum: "Activos bajo gestión (Assets Under Management) en euros",
    clientPortfolio: "Número de clientes en tu cartera de inversión o servicios financieros",
    financialProducts: "Productos financieros que ofreces (seguros, inversiones, etc.)",
    compliance: "Certificaciones de cumplimiento regulatorio y licencias financieras",
    
    // Marketing
    campaignsManaged: "Número de campañas de marketing gestionadas mensualmente",
    adSpendMonthly: "Presupuesto promedio mensual gestionado en publicidad (euros)",
    averageROAS: "Retorno de inversión publicitaria promedio (ej: 4 significa 4€ por cada 1€ invertido)",
    clientsGrowth: "Porcentaje promedio de crecimiento que generas a tus clientes",
    
    // Manufacturing/Manufactura
    manufacturingCapacity: "Capacidad máxima de producción anual en unidades de tu principal producto",
    qualityCertifications: "Certificaciones de calidad y normativas que posee tu empresa (ISO, CE, etc.)",
    automationLevel: "Porcentaje estimado de automatización en tus procesos productivos",
    
    // Real Estate/Inmobiliario
    propertiesManaged: "Número total de propiedades que gestionas o tienes en cartera",
    portfolioValue: "Valor total estimado de tu portfolio inmobiliario en euros",
    occupancyRate: "Porcentaje promedio de ocupación de tus propiedades (ej: 95 significa 95%)",
    
    // Hospitality/Hostelería
    roomsAvailable: "Número de habitaciones, mesas o espacios de capacidad disponibles",
    averageRating: "Puntuación promedio en plataformas de reseñas (Google, TripAdvisor, etc.) del 1 al 5",
    seasonalRevenue: "Porcentaje de ingresos anuales que provienen de temporada alta",
    
    // Nuevos campos Step1 - Alto impacto
    businessModel: "El modelo de negocio condiciona comparables y múltiplos sectoriales",
    revenueModel: "Diferencia ingresos recurrentes (ARR/MRR) de los transaccionales",
    recurrentShare: "Porcentaje estimado de tus ingresos que son recurrentes (0-100%)",
    customerSegment: "El segmento objetivo afecta el riesgo, pricing y ciclos de venta"
  };

  // Safe number formatter
  const formatNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0';
    }
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (isNaN(numValue)) {
      return '0';
    }
    
    return numValue.toLocaleString();
  };

  // Validar campo numérico
  const validateNumericField = (value, fieldName, t) => {
    const numValue = parseFloat(value);
    
    if (value === '' || value === null || value === undefined) {
      return { isValid: true, error: '' }; // Campo opcional
    }
    
    if (isNaN(numValue)) {
      return { isValid: false, error: t ? t('home.calculator.validation.numberRequired') : 'Must be a valid number' };
    }
    
    if (numValue < 0) {
      return { isValid: false, error: t ? t('home.calculator.validation.negativeAnnualIncome') : 'Value must be positive' };
    }
    
    // Validaciones específicas por campo
    if (fieldName === 'revenue' || fieldName === 'personalRevenue') {
      if (numValue > 100000000) { // 100M max
        return { isValid: false, error: t ? t('home.calculator.validation.maxValue', { max: '100,000,000' }) : 'Value seems excessive' };
      }
    }
    
    if (fieldName === 'recurrentShare') {
      if (numValue < 0 || numValue > 100) {
        return { isValid: false, error: t ? t('home.calculator.validation.invalidRecurrentShare') : 'Value must be between 0 and 100' };
      }
    }
    
    return { isValid: true, error: '' };
  };

  // Manejar cambios en inputs numéricos (CORREGIDO con useCallback)
  const handleNumericInput = useCallback((e, fieldName) => {
    const value = e.target.value;
    
    // Permitir solo números, puntos y comas
    const cleanValue = value.replace(/[^\d.,]/g, '');
    
    // Usar callback para evitar problemas de estado
    setFormData(prevFormData => ({
      ...prevFormData,
      [fieldName]: cleanValue
    }));
    
    // Limpiar errores de validación al escribir
    if (validationErrors[fieldName]) {
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handler específico para revenue con callback estable
  const handleRevenueInput = useCallback((e) => {
    const fieldName = brandType === 'company' ? 'revenue' : 'personalRevenue';
    handleNumericInput(e, fieldName);
  }, [brandType, handleNumericInput]);

  // ✅ STABLE HANDLERS FOR STEP1COMPONENT (NO HOOKS INSIDE)
  const onTextChange = useCallback((fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation errors when typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [validationErrors]);

  const onNumericChange = useCallback((fieldName, value) => {
    // Clean numeric input
    const cleanValue = value.replace(/[^\d.,]/g, '').replace(',', '.');
    setFormData(prev => ({
      ...prev,
      [fieldName]: cleanValue
    }));
    
    // Clear validation errors when typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [validationErrors]);

  const onSelectChange = useCallback((fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation errors when selecting
    if (validationErrors[fieldName]) {
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handler genérico para inputs de texto (OPTIMIZADO con useCallback)
  const handleTextInput = useCallback((fieldName) => (e) => {
    const value = e.target.value;
    
    // 🔍 DIAGNOSTIC LOGGING - onChange handler
    console.log(`📝 handleTextInput ${fieldName}:`, { 
      value, 
      valueLength: value.length,
      focused: document.activeElement === e.target,
      timestamp: Date.now(),
      validationErrorsKeys: Object.keys(validationErrors)
    });
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Limpiar errores de validación al escribir
    if (validationErrors[fieldName]) {
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handler genérico para selects (OPTIMIZADO con useCallback)
  const handleSelectChange = useCallback((fieldName) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Limpiar errores de validación
    if (validationErrors[fieldName]) {
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handler para checkbox (OPTIMIZADO con useCallback)
  const handleCheckboxChange = useCallback((fieldName) => (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked
    }));
  }, []);

  // Obtener progreso actual (actualizado con sentimiento)
  const getProgressPercentage = () => {
    const stepMap = {
      'lead': 0,
      'brandType': 15,
      'step1': 30,
      'step2': 45,
      'step3': 60,
      'step4': 80,
      'results': 100
    };
    return stepMap[currentStep] || 0;
  };

  // Validar paso actual
  const validateCurrentStep = () => {
    const errors = {};
    
    if (currentStep === 'step1') {
      const nameField = brandType === 'company' ? 'companyName' : 'personalName';
      const revenueField = brandType === 'company' ? 'revenue' : 'personalRevenue';
      
      if (!formData[nameField]) {
        errors[nameField] = t('home.calculator.validation.required');
      }
      
      if (!formData[revenueField]) {
        errors[revenueField] = t('home.calculator.validation.revenueRequired');
      } else {
        const validation = validateNumericField(formData[revenueField], revenueField, t);
        if (!validation.isValid) {
          errors[revenueField] = validation.error;
        }
      }
    }
    
    if (currentStep === 'step2') {
      // Step2 ya no tiene campos obligatorios tras eliminar campos duplicados
      // Los campos tangibleAssets, brandRecognition, marketPosition ahora están solo en step3
    }
    
    if (currentStep === 'step3') {
      // Validación básica para step3 - verificar que se hayan completado los campos obligatorios
      if (brandType === 'company') {
        if (!formData.tangibleAssets) {
          errors.tangibleAssets = 'Los activos tangibles son obligatorios';
        }
        if (!formData.brandRecognition) {
          errors.brandRecognition = 'El reconocimiento de marca es obligatorio';
        }
        if (!formData.marketPosition) {
          errors.marketPosition = 'La posición en el mercado es obligatoria';
        }
      } else if (brandType === 'personal') {
        // Para marca personal, validar campos específicos
        if (!formData.brandRecognition) {
          errors.brandRecognition = 'El reconocimiento de marca es obligatorio';
        }
        if (!formData.marketPosition) {
          errors.marketPosition = 'La posición en el mercado es obligatoria';
        }
      }
    }
    
    return errors;
  };

  // Navegar al siguiente paso (actualizado con sentimiento)
  const nextStep = () => {
    const errors = validateCurrentStep();
    setValidationErrors(errors);
    
    if (Object.keys(errors).length === 0) {
      const stepSequence = ['lead', 'brandType', 'step1', 'step2', 'step3', 'step4', 'results'];
      const currentIndex = stepSequence.indexOf(currentStep);
      if (currentIndex < stepSequence.length - 1) {
        setCurrentStep(stepSequence[currentIndex + 1]);
      }
    }
  };

  // Navegar al paso anterior (actualizado con sentimiento)
  const prevStep = () => {
    const stepSequence = ['lead', 'brandType', 'step1', 'step2', 'step3', 'step4', 'results'];
    const currentIndex = stepSequence.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepSequence[currentIndex - 1]);
    }
  };

  // Función para analizar el sentimiento en redes sociales (MEJORADA CON IA)
  const analyzeSentiment = async () => {
    setIsAnalyzingSentiment(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      // Preparar datos de perfiles sociales
      const profiles = {
        twitter: formData.twitterProfile || '',
        instagram: formData.instagramProfile || '',
        facebook: formData.facebookProfile || '',
        linkedin: formData.linkedinProfile || ''
      };
      
      // Filtrar perfiles vacíos
      const validProfiles = Object.fromEntries(
        Object.entries(profiles).filter(([key, value]) => value.trim() !== '')
      );
      
      // Si no hay perfiles, usar análisis mock
      if (Object.keys(validProfiles).length === 0) {
        console.warn('No se proporcionaron perfiles de redes sociales, usando análisis mock');
        const mockAnalysis = generateMockSentimentAnalysis(formData, brandType);
        setSentimentData(mockAnalysis);
        return mockAnalysis;
      }
      
      // Preparar datos para la API con métricas contextuales
      const requestData = {
        profiles: validProfiles,
        hashtags: formData.hashtags || '',
        brand_name: formData.companyName || formData.personalName || '',
        company_context: `Sector: ${formData.industry || formData.personalNiche || 'general'}, Años en mercado: ${formData.yearsInMarket || 'N/A'}`,
        language: language === 'en' ? 'en' : 'es',
        company_metrics: {
          clients: parseFloat(formData.clients) || 50,
          revenue: parseFloat(brandType === 'company' ? formData.revenue : formData.personalRevenue) || 100000,
          years_in_market: parseFloat(formData.yearsInMarket) || 3,
          social_followers: parseFloat(formData.socialFollowers) || 500
        },
        sector: brandType === 'company' ? (formData.industry || 'technology') : (formData.personalNiche || 'consulting')
      };
      
      let analysisResult;
      
      if (backendUrl) {
        try {
          // Llamar al endpoint de IA real
          const response = await fetch(`${backendUrl}/api/sentiment-analysis`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData)
          });
          
          if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
          }
          
          analysisResult = await response.json();
          console.log('✅ Análisis de sentimiento con IA real completado:', analysisResult);
          
        } catch (apiError) {
          console.warn('Error con API de sentimiento, usando análisis mock:', apiError);
          analysisResult = generateMockSentimentAnalysis(formData, brandType);
        }
      } else {
        console.warn('Backend URL no configurada, usando análisis mock');
        analysisResult = generateMockSentimentAnalysis(formData, brandType);
      }
      
      // Procesar y mejorar los datos para el frontend
      const enhancedSentimentData = enhanceSentimentDataForFrontend(analysisResult);
      
      setSentimentData(enhancedSentimentData);
      return enhancedSentimentData;
      
    } catch (error) {
      console.error('Error en análisis de sentimiento:', error);
      
      // Fallback con análisis neutro
      const fallbackAnalysis = {
        overall_sentiment: 'neutral',
        sentiment_score: 0.5,
        sentiment_breakdown: { positive: 50, neutral: 40, negative: 10 },
        posts_analyzed: 0,
        sentiment_trends: { last_week: 0, last_month: 0, last_quarter: 0 },
        key_themes: ['Sin datos suficientes para análisis'],
        sentiment_multiplier: 1.0,
        confidence_level: 'low',
        recommendations: ['Proporcione perfiles de redes sociales para análisis detallado'],
        methodology: 'Fallback Analysis'
      };
      
      setSentimentData(fallbackAnalysis);
      return fallbackAnalysis;
    } finally {
      setIsAnalyzingSentiment(false);
    }
  };
  
  // NUEVA FUNCIÓN: Generador de palabras clave contextuales por sector
  const getSectorKeywords = (sector, language = 'es') => {
    const sectorKeywords = {
      'technology': {
        'es': ['innovador', 'eficiente', 'confiable', 'profesional', 'escalable', 'disruptivo'],
        'en': ['innovative', 'efficient', 'reliable', 'professional', 'scalable', 'disruptive']
      },
      'ecommerce': {
        'es': ['rápido', 'calidad', 'recomendado', 'satisfecho', 'confiable', 'excepcional'],
        'en': ['fast', 'quality', 'recommended', 'satisfied', 'reliable', 'exceptional']
      },
      'healthcare': {
        'es': ['profesional', 'cuidadoso', 'experto', 'confianza', 'efectivo', 'empático'],
        'en': ['professional', 'caring', 'expert', 'trustworthy', 'effective', 'empathetic']
      },
      'education': {
        'es': ['inspirador', 'didáctico', 'comprensible', 'motivador', 'transformador', 'experto'],
        'en': ['inspiring', 'educational', 'clear', 'motivating', 'transformative', 'expert']
      },
      'consulting': {
        'es': ['estratégico', 'resultados', 'expertise', 'profesional', 'solutivo', 'eficaz'],
        'en': ['strategic', 'results', 'expertise', 'professional', 'effective', 'efficient']
      },
      'finance': {
        'es': ['confiable', 'transparente', 'rentable', 'seguro', 'profesional', 'eficiente'],
        'en': ['reliable', 'transparent', 'profitable', 'secure', 'professional', 'efficient']
      },
      'manufacturing': {
        'es': ['calidad', 'precisión', 'confiable', 'innovador', 'eficiente', 'sostenible'],
        'en': ['quality', 'precision', 'reliable', 'innovative', 'efficient', 'sustainable']
      },
      'hospitality': {
        'es': ['acogedor', 'excelente', 'memorable', 'cuidadoso', 'profesional', 'recomendado'],
        'en': ['welcoming', 'excellent', 'memorable', 'attentive', 'professional', 'recommended']
      }
    };
    
    return sectorKeywords[sector]?.[language] || sectorKeywords['technology'][language] || [];
  };

  // NUEVA FUNCIÓN: Generador de palabras clave basadas en rendimiento
  const getPerformanceKeywords = (clients, revenue, years, language = 'es') => {
    const performanceKeywords = [];
    const revenuePerClient = revenue / Math.max(clients, 1);
    
    if (years > 5) {
      performanceKeywords.push(language === 'es' ? 'consolidado' : 'established');
      performanceKeywords.push(language === 'es' ? 'experiencia' : 'experienced');
    }
    
    if (clients > 100) {
      performanceKeywords.push(language === 'es' ? 'popular' : 'popular');
      performanceKeywords.push(language === 'es' ? 'reconocido' : 'recognized');
    }
    
    if (revenuePerClient > 2000) {
      performanceKeywords.push(language === 'es' ? 'premium' : 'premium');
      performanceKeywords.push(language === 'es' ? 'exclusivo' : 'exclusive');
    }
    
    if (years < 3) {
      performanceKeywords.push(language === 'es' ? 'innovador' : 'innovative');
      performanceKeywords.push(language === 'es' ? 'emergente' : 'emerging');
    }
    
    return performanceKeywords;
  };

  // NUEVA FUNCIÓN: Calcular sentiment score realista basado en métricas
  const calculateRealisticSentiment = (clients, revenue, years, followers) => {
    let sentiment_score = 0.5; // Base neutral
    
    // Ajustar por rendimiento
    const revenuePerClient = revenue / Math.max(clients, 1);
    if (revenuePerClient > 2000) sentiment_score += 0.15;
    if (years > 5) sentiment_score += 0.1;
    if (clients > 100) sentiment_score += 0.1;
    if (followers > 1000) sentiment_score += 0.05;
    
    // Ajustar por ratio engagement
    const engagementRatio = followers > 0 ? Math.min(clients / followers, 1) : 0;
    if (engagementRatio > 0.1) sentiment_score += 0.1;
    
    return Math.min(sentiment_score, 0.9); // Cap en 0.9 para realismo
  };

  // NUEVA FUNCIÓN: Generar recomendaciones inteligentes contextuales
  const getIntelligentRecommendations = (formData, brandType, language = 'es') => {
    const recommendations = [];
    const clients = parseFloat(formData.clients) || 0;
    const revenue = parseFloat(brandType === 'company' ? formData.revenue : formData.personalRevenue) || 0;
    const followers = parseFloat(formData.socialFollowers) || 0;
    const years = parseFloat(formData.yearsInMarket) || 1;
    const sector = brandType === 'company' ? formData.industry : formData.personalNiche;
    
    // Recomendación basada en presencia digital
    if (followers < 1000) {
      const rec = {
        'es': `Con ${followers} seguidores, hay oportunidad de amplificar presencia digital. Considera contenido consistente sobre ${sector}.`,
        'en': `With ${followers} followers, there's opportunity to amplify digital presence. Consider consistent content about ${sector}.`
      };
      recommendations.push(rec[language]);
    }
    
    // Recomendación basada en ratio clientes/seguidores
    if (clients > 50 && followers < clients * 3) {
      const rec = {
        'es': `Tienes ${clients} clientes pero solo ${followers} seguidores. Incentiva a clientes satisfechos a seguirte en redes.`,
        'en': `You have ${clients} clients but only ${followers} followers. Encourage satisfied clients to follow you on social media.`
      };
      recommendations.push(rec[language]);
    }
    
    // Recomendación basada en ticket promedio
    const revenuePerClient = revenue / Math.max(clients, 1);
    if (revenuePerClient > 3000) {
      const rec = {
        'es': `Con ticket promedio de €${Math.round(revenuePerClient).toLocaleString()}, enfócate en testimonials de clientes premium para atraer perfil similar.`,
        'en': `With average ticket of €${Math.round(revenuePerClient).toLocaleString()}, focus on premium client testimonials to attract similar profile.`
      };
      recommendations.push(rec[language]);
    }
    
    return recommendations.slice(0, 3); // Máximo 3 recomendaciones
  };

  // NUEVA FUNCIÓN: Obtener temas clave por sector
  const getSectorThemes = (sector, language = 'es') => {
    const themes = {
      'technology': {
        'es': ['innovación tecnológica', 'eficiencia de procesos', 'soporte técnico', 'escalabilidad'],
        'en': ['technological innovation', 'process efficiency', 'technical support', 'scalability']
      },
      'ecommerce': {
        'es': ['experiencia de compra', 'logística de envíos', 'atención al cliente', 'calidad del producto'],
        'en': ['shopping experience', 'shipping logistics', 'customer service', 'product quality']
      },
      'healthcare': {
        'es': ['atención médica', 'profesionalismo', 'cuidado personalizado', 'efectividad del tratamiento'],
        'en': ['medical care', 'professionalism', 'personalized care', 'treatment effectiveness']
      },
      'education': {
        'es': ['metodología de enseñanza', 'claridad explicativa', 'transformación personal', 'resultados de aprendizaje'],
        'en': ['teaching methodology', 'explanatory clarity', 'personal transformation', 'learning outcomes']
      },
      'consulting': {
        'es': ['estrategia empresarial', 'implementación de soluciones', 'resultados medibles', 'expertise sectorial'],
        'en': ['business strategy', 'solution implementation', 'measurable results', 'sector expertise']
      },
      'finance': {
        'es': ['asesoramiento financiero', 'transparencia', 'rentabilidad', 'gestión de riesgos'],
        'en': ['financial advice', 'transparency', 'profitability', 'risk management']
      }
    };
    
    return themes[sector]?.[language] || themes['technology'][language] || [];
  };

  // NUEVA FUNCIÓN: Obtener explicación de confianza
  const getConfidenceExplanation = (sentiment_score, language = 'es') => {
    if (sentiment_score > 0.7) {
      return language === 'es' ? 
        'Alto nivel de confianza basado en datos consistentes y volumen significativo de menciones' :
        'High confidence level based on consistent data and significant mention volume';
    } else if (sentiment_score > 0.5) {
      return language === 'es' ? 
        'Nivel medio de confianza con datos representativos del período analizado' :
        'Medium confidence level with representative data from analyzed period';
    } else {
      return language === 'es' ? 
        'Nivel básico de confianza, se recomienda análisis de período más amplio' :
        'Basic confidence level, wider period analysis recommended';
    }
  };
  // FUNCIÓN MEJORADA: Generador de análisis inteligente y contextual
  const generateMockSentimentAnalysis = (formData = {}, brandType = 'company') => {
    const sector = brandType === 'company' ? (formData.industry || 'technology') : (formData.personalNiche || 'consulting');
    const clients = parseFloat(formData.clients) || 50;
    const revenue = parseFloat(brandType === 'company' ? formData.revenue : formData.personalRevenue) || 100000;
    const years = parseFloat(formData.yearsInMarket) || 3;
    const followers = parseFloat(formData.socialFollowers) || 500;
    
    console.log('🔍 DEBUGGING generateMockSentimentAnalysis:', {
      sector,
      clients,
      revenue,
      years,
      followers,
      brandType,
      language
    });
    
    // Generar palabras clave contextuales inteligentes
    const sectorKeywords = getSectorKeywords(sector, language);
    const performanceKeywords = getPerformanceKeywords(clients, revenue, years, language);
    
    console.log('🔍 Keywords generadas:', {
      sectorKeywords,
      performanceKeywords
    });
    
    // Combinar y asignar frecuencias realistas
    const trending_keywords = [
      ...sectorKeywords.slice(0, 3),
      ...performanceKeywords.slice(0, 2)
    ].map((keyword, index) => ({
      word: keyword,
      frequency: Math.max(28 - (index * 4), 8), // Frecuencias decrecientes realistas
      sentiment: 'positive'
    }));
    
    console.log('🔍 trending_keywords final:', trending_keywords);
    
    // Calcular sentiment score basado en métricas reales
    const sentiment_score = calculateRealisticSentiment(clients, revenue, years, followers);
    
    // Calcular volumen de menciones realista
    const mention_volume = Math.floor(clients * 0.8) + Math.floor(Math.random() * 50) + 20;
    
    // Generar temas clave contextuales
    const key_themes = getSectorThemes(sector, language);
    
    // Generar recomendaciones inteligentes
    const recommendations = getIntelligentRecommendations(formData, brandType, language);
    
    return {
      overall_sentiment: sentiment_score > 0.6 ? 'positive' : 'neutral',
      sentiment_score: sentiment_score,
      sentiment_breakdown: { 
        positive: Math.round(sentiment_score * 100), 
        neutral: Math.round((1 - sentiment_score) * 70), 
        negative: Math.max(5, Math.round((1 - sentiment_score) * 30))
      },
      emotion_analysis: {
        joy: Math.round(sentiment_score * 60),
        trust: Math.round(sentiment_score * 55 + 15),
        satisfaction: Math.round(sentiment_score * 50 + 10),
        excitement: Math.round(sentiment_score * 40),
        disappointment: Math.max(5, Math.round((1 - sentiment_score) * 15))
      },
      key_themes: key_themes,
      trending_keywords: trending_keywords,
      engagement_insights: {
        high_engagement_topics: sectorKeywords.slice(0, 2),
        viral_potential: sentiment_score > 0.7 ? 'high' : 'medium',
        audience_loyalty: years > 5 ? 'high' : 'medium'
      },
      recommendations: recommendations,
      confidence_level: sentiment_score > 0.7 ? 'high' : 'medium',
      analysis_metadata: {
        posts_analyzed: Math.min(clients * 2, 300),
        mention_volume: mention_volume,
        platforms_covered: ['twitter', 'instagram', 'linkedin'],
        analysis_date: new Date().toISOString(),
        time_period: language === 'es' ? '30 días' : '30 days',
        methodology: language === 'es' ? 'Análisis Contextual Avanzado' : 'Advanced Contextual Analysis',
        confidence_explanation: getConfidenceExplanation(sentiment_score, language)
      },
      engagement_analysis: {
        total_engagement: Math.round(followers * 0.08 + clients * 3),
        platform_performance: {
          twitter: { engagement_rate: Math.round((sentiment_score * 5 + 1) * 10) / 10, reach: 'medium' },
          instagram: { engagement_rate: Math.round((sentiment_score * 7 + 2) * 10) / 10, reach: 'medium' },
          linkedin: { engagement_rate: Math.round((sentiment_score * 4 + 1) * 10) / 10, reach: 'medium' }
        },
        viral_indicators: {
          share_velocity: Math.round(followers * 0.02 + 10),
          engagement_rate: Math.round((sentiment_score * 5 + 2) * 10) / 10,
          viral_potential: sentiment_score > 0.7 ? 'high' : 'medium'
        }
      },
      impact_on_brand_value: {
        multiplier: Math.round((1 + (sentiment_score - 0.5) * 0.4) * 100) / 100,
        percentage_impact: Math.round((sentiment_score - 0.5) * 40 * 10) / 10,
        description: language === 'es' ? 
          (sentiment_score > 0.6 ? 'Positivo' : 'Neutral') : 
          (sentiment_score > 0.6 ? 'Positive' : 'Neutral'),
        confidence: sentiment_score > 0.7 ? 'high' : 'medium'
      }
    };
  };
  
  // Función para mejorar los datos de sentimiento para el frontend
  const enhanceSentimentDataForFrontend = (apiData) => {
    return {
      ...apiData,
      // Compatibilidad con el frontend existente
      posts_analyzed: apiData.analysis_metadata?.posts_analyzed || 0,
      sentiment_trends: {
        last_week: apiData.sentiment_score > 0.7 ? 5 : apiData.sentiment_score > 0.5 ? 2 : -1,
        last_month: apiData.sentiment_score > 0.7 ? 12 : apiData.sentiment_score > 0.5 ? 7 : -3,
        last_quarter: apiData.sentiment_score > 0.7 ? 18 : apiData.sentiment_score > 0.5 ? 10 : -5
      },
      sentiment_multiplier: (() => {
        const rawMultiplier = apiData.impact_on_brand_value?.multiplier;
        if (rawMultiplier && !isNaN(rawMultiplier) && isFinite(rawMultiplier)) {
          return rawMultiplier;
        }
        return 1.0; // Fallback seguro
      })(),
      methodology: apiData.analysis_metadata?.methodology || 'IA Advanced Analysis'
    };
  };

  // Guardar estado en localStorage (solo para pasos avanzados, no para lead capture)
  useEffect(() => {
    console.log('💾 localStorage save effect triggered:', { currentStep, brandType, leadId });
    if (currentStep !== 'lead') {
      const saveState = {
        currentStep,
        leadData,
        leadId,
        brandType,
        formData
      };
      localStorage.setItem('calculatorState', JSON.stringify(saveState));
      console.log('💾 State saved to localStorage:', saveState);
    }
  }, [currentStep, leadId, brandType, formData]);

  // Restaurar estado desde localStorage - SOLO UNA VEZ AL MONTAR CON PROTECCIÓN CONTRA RACE CONDITIONS
  useEffect(() => {
    if (hasRestoredFromStorage) return; // Prevenir múltiples restauraciones
    
    const savedState = localStorage.getItem('calculatorState');
    if (savedState && savedState !== 'undefined') {
      try {
        const parsed = JSON.parse(savedState);
        // Solo restaurar si tenemos un leadId válido y el usuario no ha hecho selecciones
        if (parsed.leadId && parsed.currentStep !== 'lead' && currentStep === 'lead' && !brandType) {
          // Si el estado guardado es 'results' pero no hay results reales, volver a lead
          if (parsed.currentStep === 'results') {
            console.log('🔄 Estado results detectado en localStorage, no restaurando para evitar bucles');
            localStorage.removeItem('calculatorState');
            setCurrentStep('lead');
            setHasRestoredFromStorage(true);
            return;
          }
          
          console.log('🔄 Restaurando estado desde localStorage:', parsed.currentStep);
          setCurrentStep(parsed.currentStep);
          setLeadData(parsed.leadData);
          setLeadId(parsed.leadId);
          setBrandType(parsed.brandType);
          setFormData(parsed.formData);
        } else if (brandType || currentStep !== 'lead') {
          // Usuario ya ha hecho selecciones, no restaurar datos antiguos
          console.log('🔄 Usuario activo detectado, no restaurando localStorage');
        }
      } catch (error) {
        console.log('Error al cargar estado guardado:', error);
        localStorage.removeItem('calculatorState');
      }
    }
    setHasRestoredFromStorage(true); // Marcar que ya se intentó la restauración
  }, []); // SIN DEPENDENCIAS - solo ejecutar una vez al montar

  // Handle URL parameters for brand type pre-selection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const brandTypeParam = urlParams.get('brandType');
    
    if (brandTypeParam && (brandTypeParam === 'company' || brandTypeParam === 'personal') && currentStep === 'brandType' && !brandType) {
      console.log('🔗 Brand type detected from URL:', brandTypeParam);
      handleBrandTypeSelect(brandTypeParam);
    }
  }, [currentStep, brandType]);

  const handleBrandTypeSelect = (type) => {
    console.log('🔥 handleBrandTypeSelect called with type:', type);
    console.log('🔥 Current state before change:', { currentStep, brandType, hasRestoredFromStorage });
    
    // Clear any existing localStorage to prevent race conditions
    localStorage.removeItem('calculatorState');
    
    // Force immediate state updates
    setBrandType(type);
    setCurrentStep('step1');
    
    // Log state change
    console.log('🔥 State change initiated:', { newBrandType: type, newCurrentStep: 'step1' });
    
    // Force component update after state change
    setTimeout(() => {
      console.log('🔥 State after timeout:', { currentStep, brandType });
    }, 100);
  };

  // Función para generar recomendaciones personalizadas por sector
  const generateSectorRecommendations = (industry, formData, sectorMultiplier) => {
    const recommendations = [];
    
    if (!industry) return recommendations;
    
    switch (industry) {
      case 'technology':
        const activeUsers = parseFloat(formData.activeUsers) || 0;
        const churnRate = parseFloat(formData.churnRate) || 0;
        
        if (activeUsers < 5000) {
          recommendations.push({
            icon: '👥',
            title: 'Crecimiento de Base de Usuarios',
            text: 'Enfócate en estrategias de adquisición de usuarios para superar los 5,000 usuarios activos.',
            priority: 'high'
          });
        }
        if (churnRate > 5) {
          recommendations.push({
            icon: '🔄',
            title: 'Reducir Churn Rate',
            text: 'Tu tasa de abandono es alta. Implementa programas de retención y mejora la experiencia del usuario.',
            priority: 'high'
          });
        }
        if (sectorMultiplier > 0.5) {
          recommendations.push({
            icon: '🚀',
            title: 'Escala tu SaaS',
            text: 'Tienes métricas sólidas. Considera diversificar tu stack tecnológico y expandir funcionalidades.',
            priority: 'medium'
          });
        }
        break;
        
      case 'ecommerce':
        const conversionRate = parseFloat(formData.conversionRate) || 0;
        const avgOrderValue = parseFloat(formData.averageOrderValue) || 0;
        
        if (conversionRate < 2) {
          recommendations.push({
            icon: '📈',
            title: 'Optimizar Conversión',
            text: 'Tu tasa de conversión está por debajo del promedio. Mejora el checkout y la experiencia de usuario.',
            priority: 'high'
          });
        }
        if (avgOrderValue < 50) {
          recommendations.push({
            icon: '🛒',
            title: 'Incrementar Ticket Promedio',
            text: 'Implementa estrategias de upselling y cross-selling para aumentar el valor por pedido.',
            priority: 'medium'
          });
        }
        if (formData.sellsOnMarketplaces === 'no') {
          recommendations.push({
            icon: '🌐',
            title: 'Diversificar Canales',
            text: 'Considera vender en marketplaces como Amazon o MercadoLibre para ampliar tu alcance.',
            priority: 'medium'
          });
        }
        break;
        
      case 'consulting':
        const hourlyRate = parseFloat(formData.hourlyRate) || 0;
        const retention = parseFloat(formData.clientRetention) || 0;
        
        if (hourlyRate < 100) {
          recommendations.push({
            icon: '💰',
            title: 'Aumentar Tarifas',
            text: 'Tu tarifa horaria está por debajo del mercado. Considera especializarte más para justificar precios premium.',
            priority: 'high'
          });
        }
        if (retention < 70) {
          recommendations.push({
            icon: '🤝',
            title: 'Mejorar Retención',
            text: 'Enfócate en la satisfacción del cliente y seguimiento post-proyecto para mejorar retención.',
            priority: 'high'
          });
        }
        break;
        
      // NUEVAS RECOMENDACIONES PARA MARCA PERSONAL
      case 'business':
      case 'marketing':
      case 'lifestyle':
      case 'fitness':
      case 'education':
      case 'finance':
      case 'coaching':
      case 'other':
        const personalProjects = parseFloat(formData.personalProjects) || 0;
        const personalHourlyRate = parseFloat(formData.personalHourlyRate) || 0;
        const personalContent = parseFloat(formData.personalContent) || 0;
        const personalEmailList = parseFloat(formData.personalEmailList) || 0;
        
        if (personalHourlyRate < 100) {
          recommendations.push({
            icon: '💰',
            title: 'Aumentar Valor Hora',
            text: 'Tu tarifa está por debajo del mercado. Especialízate más para justificar precios premium.',
            priority: 'high'
          });
        }
        if (personalContent < 10) {
          recommendations.push({
            icon: '📝',
            title: 'Crear Más Contenido',
            text: 'Aumenta tu producción de contenido para ganar visibilidad y autoridad en tu nicho.',
            priority: 'high'
          });
        }
        if (personalEmailList < 1000) {
          recommendations.push({
            icon: '📧',
            title: 'Construir Lista Email',
            text: 'Enfócate en captar suscriptores para tu newsletter. Es clave para monetizar tu marca personal.',
            priority: 'medium'
          });
        }
        if (personalProjects < 5) {
          recommendations.push({
            icon: '🎯',
            title: 'Desarrollar Más Proyectos',
            text: 'Crea proyectos destacados que demuestren tu experiencia y generen casos de éxito.',
            priority: 'medium'
          });
        }
        break;
        
      default:
        recommendations.push({
          icon: '📊',
          title: 'Análisis Sectorial',
          text: t('results.recommendations.monitorMetrics'),
          priority: 'medium'
        });
    }
    
    // Recomendación general basada en el rendimiento
    if (sectorMultiplier < 0.2) {
      recommendations.unshift({
        icon: '⚠️',
        title: 'Mejora Urgente Requerida',
        text: 'Tus métricas sectoriales están por debajo del promedio. Prioriza las mejoras en áreas clave.',
        priority: 'critical'
      });
    }
    
    return recommendations.slice(0, 3); // Máximo 3 recomendaciones
  };

  // Función de cálculo actualizada con sentimiento y campos específicos por sector
  const handleCalculate = async () => {
    setIsCalculating(true);
    
    try {
      // VALIDACIONES MÍNIMAS - FASE 5
      const baseRevenue = brandType === 'company' 
        ? parseFloat(formData.revenue) || 0 
        : parseFloat(formData.personalRevenue) || 0;
      
      // Validar que los ingresos sean mayores a 0
      if (baseRevenue <= 0) {
        alert(t('home.calculator.validation.revenueRequired') || 'Los ingresos anuales deben ser mayores a 0');
        setIsCalculating(false);
        return;
      }
      
      // Validar campos numéricos críticos no vacíos
      const requiredNumericFields = {
        clients: formData.clients,
        yearsInMarket: formData.yearsInMarket,
        socialFollowers: formData.socialFollowers
      };
      
      for (const [field, value] of Object.entries(requiredNumericFields)) {
        if (!value || isNaN(parseFloat(value))) {
          const fieldName = t(`home.calculator.validation.fields.${field}`) || field;
          alert(t('home.calculator.validation.numericRequired', { field: fieldName }) || `El campo ${fieldName} es requerido y debe ser numérico`);
          setIsCalculating(false);
          return;
        }
      }
      
      // Analizar sentimiento si hay datos de redes sociales
      let sentimentAnalysis = null;
      if (formData.twitterProfile || formData.instagramProfile || formData.tiktokProfile || formData.facebookProfile || formData.linkedinProfile || formData.hashtags) {
        sentimentAnalysis = await analyzeSentiment({
          twitter: formData.twitterProfile,
          instagram: formData.instagramProfile,
          tiktok: formData.tiktokProfile,
          facebook: formData.facebookProfile,
          linkedin: formData.linkedinProfile,
          hashtags: formData.hashtags
        });
      }
      
      setTimeout(() => {
        // Import creator fallback helper
        const { ENABLE_CREATOR_FALLBACK, estimateAnnualForCreator } = 
          require('./domain/personal/creatorFallback');
          
        let baseRevenue = brandType === 'company' 
          ? parseFloat(formData.revenue) || 0 
          : parseFloat(formData.personalRevenue) || 0;
        
        // Fallback solo si personal-brand y no hay ingresos declarados
        if (ENABLE_CREATOR_FALLBACK && baseRevenue <= 0 && brandType === 'personal') {
          const creatorSignals = {
            instagramFollowers: parseFloat(formData.socialFollowers) || 0,
            tiktokFollowers: parseFloat(formData.tiktokFollowers) || 0
          };
          const est = estimateAnnualForCreator(creatorSignals);
          baseRevenue = Math.max(baseRevenue, est); // usamos el mayor de (0, estimación)
        }
        
        // Cálculo más sofisticado basado en todos los campos (CON VALIDACIÓN)
        let multiplier = brandType === 'company' ? 0.6 : 1.0;
        
        // VALIDACIÓN: Asegurar que multiplier inicial es válido
        if (isNaN(multiplier) || !isFinite(multiplier)) {
          multiplier = 1.0; // Fallback ultra-seguro
        }
        
        // Ajustar multiplicador basado en factores generales (CON VALIDACIÓN SEGURA)
        const clients = (() => {
          const val = parseFloat(formData.clients);
          return (!isNaN(val) && isFinite(val) && val >= 0) ? val : 0;
        })();
        const yearsInMarket = (() => {
          const val = parseFloat(formData.yearsInMarket);
          return (!isNaN(val) && isFinite(val) && val >= 0) ? val : 0;
        })();
        const followers = (() => {
          const val = parseFloat(formData.socialFollowers);
          return (!isNaN(val) && isFinite(val) && val >= 0) ? val : 0;
        })();
        const traffic = (() => {
          const val = parseFloat(formData.monthlyTraffic);
          return (!isNaN(val) && isFinite(val) && val >= 0) ? val : 0;
        })();
        // Fallback compatibility: read from step3, fallback to step2 for old sessions
        const getTangibleAssets = (form) => form?.tangibleAssets ?? null;
        const assets = parseFloat(getTangibleAssets(formData)) || 0;
        
        // Bonos por factores generales
        if (yearsInMarket > 5) multiplier += 0.1;
        if (followers > 10000) multiplier += 0.2;
        if (clients > 100) multiplier += 0.15;
        if (traffic > 10000) multiplier += 0.1;
        if (assets > 50000) multiplier += 0.05;
        
        // NUEVO: Multiplicadores específicos por sector/industria
        const industry = brandType === 'company' ? formData.industry : formData.personalNiche;
        let sectorMultiplier = 0;
        let sectorBreakdown = {};
        
        // Aplicar lógica específica por sector
        if (brandType === 'company' && industry) {
          switch (industry) {
            case 'technology':
              const activeUsers = parseFloat(formData.activeUsers) || 0;
              const arr = parseFloat(formData.arr) || 0;
              const churnRate = parseFloat(formData.churnRate) || 0;
              
              // Bonificaciones por métricas SaaS
              if (activeUsers > 1000) sectorMultiplier += 0.3;
              if (activeUsers > 10000) sectorMultiplier += 0.2;
              if (arr > 100000) sectorMultiplier += 0.4;
              if (churnRate < 5) sectorMultiplier += 0.15; // Churn rate bajo es bueno
              if (formData.techStack && formData.techStack.length > 10) sectorMultiplier += 0.1;
              
              sectorBreakdown = {
                activeUsers,
                arr,
                churnRate: churnRate || 'N/A',
                techStack: formData.techStack || 'N/A'
              };
              break;
              
            case 'ecommerce':
              const products = parseFloat(formData.productsInCatalog) || 0;
              const conversionRate = parseFloat(formData.conversionRate) || 0;
              const avgOrderValue = parseFloat(formData.averageOrderValue) || 0;
              const returnRate = parseFloat(formData.returnRate) || 0;
              const numberOfMarketplaces = parseFloat(formData.numberOfMarketplaces) || 0;
              const marketplaceRevenue = parseFloat(formData.marketplaceRevenue) || 0;
              
              // Bonificaciones por métricas de e-commerce
              if (products > 100) sectorMultiplier += 0.2;
              if (conversionRate > 2) sectorMultiplier += 0.25;
              if (conversionRate > 5) sectorMultiplier += 0.15;
              if (avgOrderValue > 50) sectorMultiplier += 0.1;
              if (returnRate < 10) sectorMultiplier += 0.1; // Tasa de devolución baja es buena
              
              // NUEVAS bonificaciones por marketplaces
              if (formData.sellsOnMarketplaces === 'yes' || formData.sellsOnMarketplaces === 'both') {
                sectorMultiplier += 0.15; // Diversificación de canales
                if (numberOfMarketplaces > 1) sectorMultiplier += 0.1;
                if (numberOfMarketplaces > 3) sectorMultiplier += 0.05;
                
                // Bonificación balanceada por marketplaces (no dependencia total)
                if (marketplaceRevenue > 20 && marketplaceRevenue < 70) {
                  sectorMultiplier += 0.2; // Balance óptimo
                } else if (marketplaceRevenue > 0) {
                  sectorMultiplier += 0.1; // Algo de diversificación
                }
              }
              
              sectorBreakdown = {
                products,
                conversionRate: conversionRate || 'N/A',
                avgOrderValue,
                returnRate: returnRate || 'N/A',
                sellsOnMarketplaces: formData.sellsOnMarketplaces || 'N/A',
                numberOfMarketplaces: numberOfMarketplaces || 'N/A',
                marketplaceRevenue: marketplaceRevenue || 'N/A'
              };
              break;
              
            case 'consulting':
              const projects = parseFloat(formData.completedProjects) || 0;
              const hourlyRate = parseFloat(formData.hourlyRate) || 0;
              const retention = parseFloat(formData.clientRetention) || 0;
              
              // Bonificaciones por métricas de consultoría
              if (projects > 20) sectorMultiplier += 0.2;
              if (hourlyRate > 100) sectorMultiplier += 0.3;
              if (hourlyRate > 200) sectorMultiplier += 0.2;
              if (retention > 80) sectorMultiplier += 0.25;
              if (formData.certifications && formData.certifications.length > 10) sectorMultiplier += 0.15;
              
              sectorBreakdown = {
                projects,
                hourlyRate,
                retention: retention || 'N/A',
                certifications: formData.certifications || 'N/A'
              };
              break;
              
            case 'healthcare':
              const patients = parseFloat(formData.patients) || 0;
              
              // Bonificaciones por métricas de salud
              if (patients > 500) sectorMultiplier += 0.2;
              if (patients > 1000) sectorMultiplier += 0.15;
              if (formData.specializations && formData.specializations.length > 5) sectorMultiplier += 0.2;
              if (formData.medicalLicenses && formData.medicalLicenses.length > 10) sectorMultiplier += 0.1;
              
              sectorBreakdown = {
                patients,
                specializations: formData.specializations || 'N/A',
                licenses: formData.medicalLicenses || 'N/A'
              };
              break;
              
            case 'education':
              const students = parseFloat(formData.students) || 0;
              const courses = parseFloat(formData.courses) || 0;
              const completionRate = parseFloat(formData.completionRate) || 0;
              
              // Bonificaciones por métricas educativas
              if (students > 100) sectorMultiplier += 0.2;
              if (courses > 5) sectorMultiplier += 0.15;
              if (completionRate > 80) sectorMultiplier += 0.25;
              if (formData.accreditations && formData.accreditations.length > 10) sectorMultiplier += 0.1;
              
              sectorBreakdown = {
                students,
                courses,
                completionRate: completionRate || 'N/A',
                accreditations: formData.accreditations || 'N/A'
              };
              break;
              
            case 'finance':
              const aum = parseFloat(formData.aum) || 0;
              const clientPortfolio = parseFloat(formData.clientPortfolio) || 0;
              
              // Bonificaciones por métricas financieras
              if (aum > 1000000) sectorMultiplier += 0.4; // AUM es clave en finanzas
              if (aum > 5000000) sectorMultiplier += 0.3;
              if (clientPortfolio > 50) sectorMultiplier += 0.2;
              if (formData.compliance && formData.compliance.length > 10) sectorMultiplier += 0.15;
              
              sectorBreakdown = {
                aum,
                clientPortfolio,
                products: formData.financialProducts || 'N/A',
                compliance: formData.compliance || 'N/A'
              };
              break;
              
            case 'manufacturing':
              const capacity = parseFloat(formData.manufacturingCapacity) || 0;
              const automationLevel = parseFloat(formData.automationLevel) || 0;
              
              // Bonificaciones por métricas de manufactura
              if (capacity > 10000) sectorMultiplier += 0.2;
              if (capacity > 50000) sectorMultiplier += 0.15;
              if (automationLevel > 50) sectorMultiplier += 0.25;
              if (automationLevel > 80) sectorMultiplier += 0.2;
              if (formData.qualityCertifications && formData.qualityCertifications.length > 5) sectorMultiplier += 0.15;
              
              sectorBreakdown = {
                capacity,
                automationLevel: automationLevel || 'N/A',
                certifications: formData.qualityCertifications || 'N/A'
              };
              break;
              
            case 'real_estate':
              const properties = parseFloat(formData.propertiesManaged) || 0;
              const portfolioValue = parseFloat(formData.portfolioValue) || 0;
              const occupancyRate = parseFloat(formData.occupancyRate) || 0;
              
              // Bonificaciones por métricas inmobiliarias
              if (properties > 50) sectorMultiplier += 0.2;
              if (properties > 200) sectorMultiplier += 0.15;
              if (portfolioValue > 2000000) sectorMultiplier += 0.3;
              if (portfolioValue > 10000000) sectorMultiplier += 0.25;
              if (occupancyRate > 90) sectorMultiplier += 0.2;
              
              sectorBreakdown = {
                properties,
                portfolioValue,
                occupancyRate: occupancyRate || 'N/A'
              };
              break;
              
            case 'hospitality':
              const rooms = parseFloat(formData.roomsAvailable) || 0;
              const rating = parseFloat(formData.averageRating) || 0;
              const seasonalRevenue = parseFloat(formData.seasonalRevenue) || 0;
              
              // Bonificaciones por métricas de hostelería
              if (rooms > 50) sectorMultiplier += 0.2;
              if (rooms > 200) sectorMultiplier += 0.15;
              if (rating > 4.0) sectorMultiplier += 0.25;
              if (rating > 4.5) sectorMultiplier += 0.2;
              if (seasonalRevenue > 60) sectorMultiplier += 0.15;
              
              sectorBreakdown = {
                rooms,
                rating: rating || 'N/A',
                seasonalRevenue: seasonalRevenue || 'N/A'
              };
              break;

            case 'marketing':
              const campaigns = parseFloat(formData.campaignsManaged) || 0;
              const adSpend = parseFloat(formData.adSpendMonthly) || 0;
              const roas = parseFloat(formData.averageROAS) || 0;
              const growth = parseFloat(formData.clientsGrowth) || 0;
              
              // Bonificaciones por métricas de marketing
              if (campaigns > 20) sectorMultiplier += 0.2;
              if (campaigns > 50) sectorMultiplier += 0.15;
              if (adSpend > 10000) sectorMultiplier += 0.25;
              if (adSpend > 30000) sectorMultiplier += 0.2;
              if (roas > 3) sectorMultiplier += 0.3;
              if (roas > 5) sectorMultiplier += 0.25;
              if (growth > 20) sectorMultiplier += 0.2;
              
              sectorBreakdown = {
                campaigns,
                adSpend,
                roas: roas || 'N/A',
                growth: growth || 'N/A'
              };
              break;
              
            default:
              // Para sectores "other" o no especificados, usar bonificación base
              sectorMultiplier = 0.05;
              break;
          }
        }
        
        // NUEVA: Lógica específica para MARCA PERSONAL
        if (brandType === 'personal' && industry) {
          const projects = parseFloat(formData.personalProjects) || 0;
          const hourlyRate = parseFloat(formData.personalHourlyRate) || 0;
          const clients = parseFloat(formData.personalClients) || 0;
          const courses = parseFloat(formData.personalCourses) || 0;
          const speaking = parseFloat(formData.personalSpeaking) || 0;
          const collaborations = parseFloat(formData.personalCollaborations) || 0;
          const content = parseFloat(formData.personalContent) || 0;
          const emailList = parseFloat(formData.personalEmailList) || 0;
          
          // Bonificaciones generales para marca personal
          if (projects > 10) sectorMultiplier += 0.2;
          if (hourlyRate > 100) sectorMultiplier += 0.3;
          if (hourlyRate > 200) sectorMultiplier += 0.2;
          if (clients > 15) sectorMultiplier += 0.25;
          if (courses > 2) sectorMultiplier += 0.2;
          if (speaking > 8) sectorMultiplier += 0.15;
          if (collaborations > 5) sectorMultiplier += 0.15;
          if (content > 15) sectorMultiplier += 0.1;
          if (emailList > 1000) sectorMultiplier += 0.2;
          if (emailList > 5000) sectorMultiplier += 0.15;
          
          // Bonificación extra por diversificación de actividades
          const activeAreas = [
            projects > 0 ? 1 : 0,
            courses > 0 ? 1 : 0, 
            speaking > 0 ? 1 : 0,
            collaborations > 0 ? 1 : 0,
            content > 10 ? 1 : 0,
            emailList > 500 ? 1 : 0
          ].reduce((sum, val) => sum + val, 0);
          
          if (activeAreas >= 4) sectorMultiplier += 0.25; // Marca diversificada
          
          sectorBreakdown = {
            projects,
            hourlyRate,
            clients,
            courses,
            speaking,
            collaborations,
            content,
            emailList,
            activeAreas
          };
        }
        
        // Aplicar multiplicador sectorial
        multiplier += sectorMultiplier;
        
        // Aplicar multiplicador de sentimiento (CON VALIDACIÓN SEGURA)
        if (sentimentAnalysis && sentimentAnalysis.sentiment_multiplier) {
          const sentimentMult = sentimentAnalysis.sentiment_multiplier;
          // Validar que sentiment_multiplier es un número válido
          if (!isNaN(sentimentMult) && isFinite(sentimentMult) && sentimentMult > 0) {
            multiplier = multiplier * sentimentMult;
          }
        }
        
        // VALIDACIÓN FINAL: Asegurar que multiplier es válido
        if (isNaN(multiplier) || !isFinite(multiplier) || multiplier <= 0) {
          console.warn('Multiplier inválido detectado, usando fallback:', multiplier);
          multiplier = brandType === 'company' ? 0.6 : 1.0; // Fallback seguro
        }
        
        // VALIDACIÓN FINAL: Asegurar que baseRevenue es válido  
        if (isNaN(baseRevenue) || !isFinite(baseRevenue) || baseRevenue < 0) {
          console.warn('BaseRevenue inválido detectado, usando fallback:', baseRevenue);
          baseRevenue = 0;
        }
        
        // NUEVO: Generar recomendaciones personalizadas por sector
        const sectorRecommendations = generateSectorRecommendations(industry, formData, sectorMultiplier);
        
        const calculatedValue = Math.round(baseRevenue * multiplier);
        
        setResults({
          totalValue: calculatedValue,
          baseRevenue: baseRevenue,
          multiplier: multiplier.toFixed(2) + 'x',
          breakdown: {
            clients,
            yearsInMarket,
            followers,
            traffic,
            assets,
            sentiment: sentimentAnalysis ? sentimentAnalysis.sentiment_score : null,
            sector: industry || 'N/A',
            sectorMetrics: sectorBreakdown
          },
          sentimentData: sentimentAnalysis,
          recommendations: sectorRecommendations // NUEVO: Recomendaciones incluidas
        });
        setCurrentStep('results');
        setIsCalculating(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error en el cálculo:', error);
      setIsCalculating(false);
    }
  };

  // Indicador de progreso
  const ProgressIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Progreso</h3>
        <span className="text-purple-400 font-semibold">{getProgressPercentage()}%</span>
      </div>
      
      <div className="w-full bg-slate-700 rounded-full h-3">
        <div 
          className="bg-gradient-to-r from-purple-600 to-pink-600 h-3 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${getProgressPercentage()}%` }}
        ></div>
      </div>
      
      {/* Indicadores de pasos */}
      <div className="flex justify-between mt-4">
        {formSteps.map((step, index) => {
          const isCompleted = getProgressPercentage() > (index + 1) * 20;
          const isCurrent = currentStep === step.id;
          
          return (
            <div key={step.id} className={`flex flex-col items-center space-y-1 ${
              isCompleted || isCurrent ? 'text-purple-400' : 'text-gray-500'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                isCompleted ? 'bg-purple-600 text-white' :
                isCurrent ? 'bg-purple-600/50 border-2 border-purple-400 text-white' :
                'bg-slate-700 text-gray-400'
              }`}>
                {isCompleted ? '✓' : index + 1}
              </div>
              <div className="text-xs text-center hidden sm:block">
                <div className="font-medium">{step.title}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const BrandTypeSelector = () => (
    <div className="text-center px-4">
      <div className="mb-8">
        <p className="text-gray-300 mb-2">{t('home.calculator.greeting').replace('{name}', leadData.name)}</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          {t('home.calculator.mainTitle')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">{t('home.calculator.advanced')}</span>
        </h2>
        <p className="text-lg sm:text-xl text-gray-300 mb-12">
          {t('home.calculator.brandTypeSubtitle')}
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
        <div
          onClick={() => handleBrandTypeSelect('company')}
          className="group cursor-pointer bg-gradient-to-br from-purple-600/20 to-blue-600/20 hover:from-purple-600/40 hover:to-blue-600/40 border-2 border-purple-500/30 hover:border-purple-400 rounded-3xl p-6 sm:p-8 transition-all duration-500 transform hover:scale-105"
        >
          <div className="text-center">
            <div className="text-6xl sm:text-8xl mb-6">🏢</div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('home.calculator.brandCompany')}</h3>
            <p className="text-gray-300 text-base sm:text-lg mb-4">
              {t('home.calculator.brandCompanyDesc')}
            </p>
            <div className="text-sm text-gray-400 space-y-1">
              <p>• {t('home.calculator.brandCompanyAnalysis')}</p>
              <p>• {t('home.calculator.brandCompanyAssets')}</p>
              <p>• {t('home.calculator.brandCompanyMetrics')}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => handleBrandTypeSelect('personal')}
          className="group cursor-pointer bg-gradient-to-br from-pink-600/20 to-orange-600/20 hover:from-pink-600/40 hover:to-orange-600/40 border-2 border-pink-500/30 hover:border-pink-400 rounded-3xl p-6 sm:p-8 transition-all duration-500 transform hover:scale-105"
        >
          <div className="text-center">
            <div className="text-6xl sm:text-8xl mb-6">👤</div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('home.calculator.brandPersonal')}</h3>
            <p className="text-gray-300 text-base sm:text-lg mb-4">
              {t('home.calculator.brandPersonalDesc')}
            </p>
            <div className="text-sm text-gray-400 space-y-1">
              <p>• {t('home.calculator.brandPersonalSocial')}</p>
              <p>• {t('home.calculator.brandPersonalContent')}</p>
              <p>• {t('home.calculator.brandPersonalMonetization')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ✅ Components moved outside - Step3Component and Step4Component now live above main component

  // Función para generar comparación sectorial (CON VERIFICACIONES SEGURAS)
  const generateSectorComparison = (userValue, industry, companySize) => {
    // Verificar que userValue existe y es válido
    if (!userValue || isNaN(userValue) || userValue <= 0) {
      // Valores por defecto seguros si no hay datos
      return {
        competitors: [
          { name: 'Promedio del Sector', value: 150000, isAverage: true },
          { name: 'Tu Marca', value: userValue || 0, isUser: true }
        ],
        sectorAverage: 150000,
        userPercentile: 50,
        industry: industry || 'other',
        size: 'medium',
        isAboveAverage: false,
        difference: 0
      };
    }

    // Datos base por sector (promedios reales aproximados)
    const sectorAverages = {
      technology: { small: 180000, medium: 850000, large: 4200000 },
      ecommerce: { small: 120000, medium: 650000, large: 3100000 },
      consulting: { small: 90000, medium: 420000, large: 2800000 },
      healthcare: { small: 200000, medium: 950000, large: 4800000 },
      education: { small: 75000, medium: 350000, large: 1900000 },
      finance: { small: 250000, medium: 1200000, large: 6500000 },
      marketing: { small: 110000, medium: 520000, large: 2400000 },
      other: { small: 140000, medium: 680000, large: 3200000 }
    };

    // Determinar tamaño de empresa basado en ingresos (con verificación segura)
    const revenue = results?.baseRevenue || userValue;
    let size = 'small';
    if (revenue > 500000) size = 'medium';
    if (revenue > 2000000) size = 'large';

    // Obtener promedio sectorial
    const sectorData = sectorAverages[industry] || sectorAverages.other;
    const sectorAverage = sectorData[size];

    // Generar datos de comparación con variación realista
    const variance = 0.15; // 15% de variación
    const competitors = [
      { name: 'Promedio del Sector', value: sectorAverage, isAverage: true },
      { name: 'Competidor A', value: Math.round(sectorAverage * (1 + Math.random() * variance * 2 - variance)) },
      { name: 'Competidor B', value: Math.round(sectorAverage * (1 + Math.random() * variance * 2 - variance)) },
      { name: 'Competidor C', value: Math.round(sectorAverage * (1 + Math.random() * variance * 2 - variance)) },
      { name: 'Tu Marca', value: userValue, isUser: true }
    ];

    // Ordenar por valor
    competitors.sort((a, b) => b.value - a.value);

    // Calcular posición percentil
    const userPosition = competitors.findIndex(c => c.isUser) + 1;
    const percentile = Math.round(((competitors.length - userPosition) / (competitors.length - 1)) * 100);

    return {
      competitors,
      sectorAverage,
      userPercentile: percentile,
      industry: industry || 'other',
      size,
      isAboveAverage: userValue > sectorAverage,
      difference: Math.round(((userValue - sectorAverage) / sectorAverage) * 100)
    };
  };

// ResultsComponent moved outside with safe guards - NO HOOKS ALLOWED
const ResultsComponent = React.memo(function ResultsComponent({
  results,
  sentimentData,
  brandType,
  isCalculating,
  formatNumber,
  formData,
  // Funciones necesarias para los botones de acción
  setCurrentStep,
  setResults,
  setLeadData,
  setLeadId,
  setBrandType,
  setFormData,
  setValidationErrors
}) {
  // Get translations and language context
  const { t, language } = useTranslations();
  
  // Create locale-aware formatters
  const formatCurrencyLocale = (value) => formatCurrency(value, language);
  const formatNumberLocale = (value) => formatNumber(value, language);
  
  // Format money for sharing with proper locale
  const formatMoney = (value, locale = language, currency = "EUR") => {
    return new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-GB', { 
      style: "currency", 
      currency 
    }).format(value);
  };

  // Function to determine if sentiment data is real (not mock/fallback)
  const hasRealSentimentData = (data) => {
    if (!data) return false;
    
    // First check if backend explicitly marked this as real data
    if (data.is_real === false) return false;
    if (data.is_real === true) {
      // For real data, verify it meets our criteria
      const hasEnoughPosts = (data.analysis_metadata?.posts_analyzed || 0) >= 10;
      
      // Check if last_updated is within 72 hours
      const lastUpdated = data.analysis_metadata?.analysis_date ? 
        new Date(data.analysis_metadata.analysis_date) : null;
      const isRecent = lastUpdated ? 
        (Date.now() - lastUpdated.getTime()) <= (72 * 60 * 60 * 1000) : false;
      
      return hasEnoughPosts && isRecent;
    }
    
    // Fallback: Check if it's mock data by looking for known mock patterns (for frontend-generated mocks)
    const isMockData = (
      (data.sentiment_score === 0.72 && data.analysis_metadata?.methodology === 'Mock Advanced Analysis') ||
      (data.sentiment_score === 0.75 && data.analysis_metadata?.methodology === 'Análisis Mock Avanzado') ||
      (data.sentiment_score === 0.5 && data.analysis_metadata?.methodology === 'Fallback Analysis') ||
      data.analysis_metadata?.methodology?.includes('Mock')
    );
    
    return !isMockData;
  };

  // Determine if we should show real sentiment data
  const showRealSentiment = hasRealSentimentData(sentimentData);

  // Helper for safe array handling from i18n
  const asArray = (v) => (Array.isArray(v) ? v : []);
  
  // Helper for safe translation array access
  const tArray = (key) => {
    const v = t(key, { returnObjects: true });
    return Array.isArray(v) ? v : [];
  };
  // Safe guards: handle null/undefined results
  if (!results) {
    if (isCalculating) {
      return (
        <div className="space-y-8">
          <div className="text-center">
            <div className="text-6xl mb-4">⏳</div>
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('results.loading.calculating')}
            </h2>
            <p className="text-xl text-gray-300">
              {t('results.loading.analyzing', { 
                brandType: brandType === 'company' ? t('results.loading.company') : t('results.loading.personalBrand') 
              })}
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-8">
          <div className="text-center">
            <div className="text-6xl mb-4">📊</div>
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('results.loading.completeSteps')}
            </h2>
            <p className="text-xl text-gray-300">
              {t('results.loading.needAllSteps')}
            </p>
            <div className="mt-6">
              <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                {t('results.loading.backToForm')}
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Safe data extraction with defaults
  const totalValue = results?.totalValue ?? 0;
  const baseRevenue = results?.baseRevenue ?? 0;
  // FIXED: Better default for multiplier based on brand type
  const multiplierValue = results?.multiplier ?? (brandType === 'personal' ? '1.0x' : '0.6x');
  const breakdown = results?.breakdown ?? {};
  const sectorMetrics = breakdown?.sectorMetrics ?? {};
  const sector = breakdown?.sector ?? 'other';

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-4xl font-bold text-white mb-4">
          {t('results.headline')}: <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">
            {formatCurrencyLocale(totalValue)}
          </span>
        </h2>
        <p className="text-xl text-gray-300">
          {(() => {
            const personalizedInsights = generatePersonalizedInsights(formData, results, brandType, language);
            return personalizedInsights.heroMessage || (
              brandType === 'personal' 
                ? t('calcResults.subtitle', { entity: t('calcResults.entity.brand') })
                : t('calcResults.subtitle', { entity: t('calcResults.entity.company') })
            );
          })()}
        </p>

        {/* Indicador de Confianza */}
        <div className="mt-6 p-4 bg-green-900/20 border border-green-500/30 rounded-xl">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <p className="text-green-300 text-sm leading-relaxed">
                {brandType === 'personal' 
                  ? t('home.calculator.confidenceIndicator.personal') || '✅ Nivel de precisión: 85% para marcas personales similares en tu nicho | 👤 Base de datos: 300+ marcas personales analizadas'
                  : t('home.calculator.confidenceIndicator.company') || '✅ Nivel de precisión: 85% para empresas similares en tu sector | 🏢 Base de datos: 500+ empresas analizadas'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA de Conversión Potente */}
      <div className="bg-gradient-to-r from-orange-600/20 to-red-600/20 rounded-2xl p-6 border border-orange-500/30">
        <div className="text-center">
          <div className="text-3xl mb-3">📊</div>
          <h3 className="text-xl font-bold text-white mb-3">
            {t('home.calculator.conversionTitle') || '¿Quieres validar estos números?'}
          </h3>
          <p className="text-gray-300 mb-6 leading-relaxed">
            {t('home.calculator.conversionSubtitle') || 'Esta estimación es tu punto de partida. Para decisiones críticas como inversión o crecimiento, nuestros consultores te ofrecen análisis detallado y estrategias específicas.'}
          </p>
          <a
            href="https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-orange-700 hover:to-red-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            {t('home.calculator.conversionCta') || '📞 Validar con Consultor (30 min gratis)'}
          </a>
        </div>
      </div>

      {/* Desglose detallado */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
        <h3 className="text-xl font-bold text-white mb-4">📊 {t('results.sections.analysisBreakdown')}</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-300">{t('results.labels.baseRevenue')}:</span>
            <span className="text-white font-semibold">{formatCurrencyLocale(baseRevenue)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-300">{t('results.labels.finalMultiplier')}:</span>
            <span className="text-purple-400 font-semibold">{multiplierValue}</span>
          </div>
          {(breakdown?.yearsInMarket ?? 0) > 5 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-300">• Experiencia (+10%):</span>
              <span className="text-green-400">+{formatCurrencyLocale(Math.round(baseRevenue * 0.1))}</span>
            </div>
          )}
          {(breakdown?.followers ?? 0) > 10000 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-300">• Audiencia Sólida (+20%):</span>
              <span className="text-green-400">+{formatCurrencyLocale(Math.round(baseRevenue * 0.2))}</span>
            </div>
          )}
          {(breakdown?.clients ?? 0) > 100 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-300">• {t('results.labels.clientBase')} (+15%):</span>
              <span className="text-green-400">+{formatCurrencyLocale(Math.round(baseRevenue * 0.15))}</span>
            </div>
          )}
          <div className="flex justify-between items-center border-t border-gray-600 pt-3">
            <span className="text-gray-300 font-semibold">{t('results.labels.totalValue')}:</span>
            <span className="text-green-400 font-bold text-xl">{formatCurrencyLocale(totalValue)}</span>
          </div>
        </div>
      </div>

      {/* NUEVA SECCIÓN: Métricas Específicas por Sector - MEJORADO CON ESTADO VACÍO */}
      {brandType === 'company' && sector && sector !== 'other' && (
        <div className="bg-gradient-to-r from-teal-600/20 to-cyan-600/20 rounded-2xl p-6 border border-teal-500/30">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <span className="text-2xl mr-2">🎯</span>
            {t('results.sections.sectorAnalysis')}
          </h3>
          
          {sectorMetrics && Object.keys(sectorMetrics).length > 0 ? (
            // CON DATOS: Mostrar métricas reales
            <div className="bg-slate-700/30 p-4 rounded-lg">
              <h4 className="font-semibold text-teal-400 mb-3">
                📈 {
                  sector === 'technology' ? t('results.sector.technologyMetrics') :
                  sector === 'ecommerce' ? t('results.sector.ecommerceMetrics') :
                  sector === 'consulting' ? t('results.sector.consultingMetrics') :
                  sector === 'healthcare' ? t('results.sector.healthcareMetrics') :
                  sector === 'education' ? t('results.sector.educationMetrics') :
                  sector === 'finance' ? t('results.sector.financeMetrics') :
                  sector === 'marketing' ? t('results.sector.marketingMetrics') : 
                  t('results.sectors.technology')
                }
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Renderizar métricas específicas por sector */}
                {sector === 'technology' && (
                  <>
                    {(sectorMetrics?.activeUsers ?? 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">👥 Usuarios Activos:</span>
                        <span className="text-white font-semibold">{formatNumberLocale(sectorMetrics.activeUsers)}</span>
                      </div>
                    )}
                    {(sectorMetrics?.arr ?? 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">💰 ARR:</span>
                        <span className="text-white font-semibold">{formatCurrencyLocale(sectorMetrics.arr)}</span>
                      </div>
                    )}
                    {(sectorMetrics?.churnRate ?? 'N/A') !== 'N/A' && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">📉 Churn Rate:</span>
                        <span className="text-white font-semibold">{sectorMetrics.churnRate}%</span>
                      </div>
                    )}
                    {(sectorMetrics?.techStack ?? 'N/A') !== 'N/A' && (
                      <div className="col-span-1 md:col-span-2">
                        <span className="text-gray-300">⚡ Tech Stack: </span>
                        <span className="text-white font-semibold">{sectorMetrics.techStack}</span>
                      </div>
                    )}
                  </>
                )}
                
                {sector === 'ecommerce' && (
                  <>
                    {(sectorMetrics?.products ?? 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">📦 Productos:</span>
                        <span className="text-white font-semibold">{formatNumberLocale(sectorMetrics.products)}</span>
                      </div>
                    )}
                    {(sectorMetrics?.conversionRate ?? 'N/A') !== 'N/A' && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">💹 Conversión:</span>
                        <span className="text-white font-semibold">{sectorMetrics.conversionRate}%</span>
                      </div>
                    )}
                    {(sectorMetrics?.avgOrderValue ?? 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">🛒 Ticket Promedio:</span>
                        <span className="text-white font-semibold">€{sectorMetrics.avgOrderValue}</span>
                      </div>
                    )}
                    {(sectorMetrics?.returnRate ?? 'N/A') !== 'N/A' && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">↩️ Devoluciones:</span>
                        <span className="text-white font-semibold">{sectorMetrics.returnRate}%</span>
                      </div>
                    )}
                  </>
                )}
                
                {/* Otras secciones sectoriales existentes... */}
                {sector === 'consulting' && (
                  <>
                    {(sectorMetrics?.totalProjects ?? 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">📊 Proyectos:</span>
                        <span className="text-white font-semibold">{sectorMetrics.totalProjects}</span>
                      </div>
                    )}
                    {(sectorMetrics?.hourlyRate ?? 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">💰 Tarifa/Hora:</span>
                        <span className="text-white font-semibold">€{sectorMetrics.hourlyRate}</span>
                      </div>
                    )}
                    {(sectorMetrics?.clientRetentionRate ?? 'N/A') !== 'N/A' && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">🔄 Retención:</span>
                        <span className="text-white font-semibold">{sectorMetrics.clientRetentionRate}%</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            // SIN DATOS: Estado vacío con CTA
            <div className="text-center py-8">
              <div className="text-5xl mb-4">📊</div>
              <h4 className="text-xl font-bold text-white mb-2">
                {t('results.sections.sectorEmpty.title')}
              </h4>
              <p className="text-gray-300 mb-4 max-w-md mx-auto">
                {t('results.sections.sectorEmpty.subtitle')}
              </p>
              <p className="text-sm text-gray-400 mb-6">
                {t('results.sections.sectorEmpty.description')}
              </p>
              
              <div className="bg-slate-700/30 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <h5 className="text-sm font-semibold text-teal-400 mb-3">✨ Obtendrás:</h5>
                <ul className="text-sm text-gray-300 space-y-2 text-left">
                  {tArray('results.sections.sectorEmpty.benefits').map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-400 mr-2">•</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              
              <button 
                onClick={() => setCurrentStep('step1')}
                className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-300"
              >
                {t('results.sections.sectorEmpty.cta')}
              </button>
              
              <p className="text-xs text-gray-400 mt-4">
                Sector: <span className="capitalize font-semibold text-teal-400">{sector}</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Nueva sección: Análisis de Sentimiento Mejorado con IA */}
      <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-2xl p-6 border border-cyan-500/30">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <span className="text-2xl mr-2">😊</span>
          {t('results.sections.sentimentAnalysis')}
        </h3>
        
        {showRealSentiment ? (
          // Real sentiment data UI
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Score general mejorado */}
              <div className="bg-slate-700/30 p-4 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-3">📊 {t('results.labels.generalScore')}</h4>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-2">
                    {(sentimentData?.sentiment_score * 10).toFixed(1)}/10
                  </div>
                  <div className="w-full bg-slate-600 rounded-full h-3 mb-2">
                    <div 
                      className="bg-gradient-to-r from-cyan-500 to-cyan-400 h-3 rounded-full transition-all duration-500" 
                      style={{
                        width: `${Math.max(20, Math.min(90, sentimentData?.sentiment_score * 100))}%`
                      }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-300">
                    {sentimentData?.overall_sentiment === 'positive' ? t('results.sentiment.veryPositiveSentiment') : 
                     sentimentData?.overall_sentiment === 'negative' ? t('results.sentiment.negativeSentiment') : 
                     t('results.sentiment.neutralSentiment')}
                  </p>
                  <div className="mt-2">
                    <span className="text-xs text-gray-400">
                      Powered by {sentimentData?.methodology}
                    </span>
                  </div>
                </div>
              </div>

              {/* Desglose de sentimientos mejorado */}
              <div className="bg-slate-700/30 p-4 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-3">💬 {t('results.sentiment.breakdown')}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">😊 {t('results.sentiment.positive')}:</span>
                    <span className="text-green-400 font-semibold">
                      {sentimentData?.sentiment_breakdown?.positive}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">😐 {t('results.sentiment.neutral')}:</span>
                    <span className="text-gray-400 font-semibold">
                      {sentimentData?.sentiment_breakdown?.neutral}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">😞 {t('results.sentiment.negative')}:</span>
                    <span className="text-red-400 font-semibold">
                      {sentimentData?.sentiment_breakdown?.negative}%
                    </span>
                  </div>
                </div>
                
                {/* Mostrar análisis de emociones si está disponible */}
                {sentimentData?.emotion_analysis && (
                  <div className="mt-4 pt-3 border-t border-slate-600">
                    <h5 className="text-xs font-semibold text-cyan-300 mb-2">🎭 {t('results.sentiment.emotionalAnalysis')}</h5>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(sentimentData.emotion_analysis).slice(0, 3).map(([emotion, value]) => (
                        <span 
                          key={emotion}
                          className="bg-cyan-500/20 text-cyan-300 px-2 py-1 rounded text-xs"
                        >
                          {emotion}: {value}%
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Tendencias temporales */}
              <div className="bg-slate-700/30 p-4 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-3">📈 {t('results.labels.trend')}</h4>
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-green-400 text-2xl">📈</span>
                  <div className="text-center">
                    <div className="text-xl font-bold text-green-400">
                      {sentimentData?.sentiment_trends?.last_month > 0 ? '+' : ''}{sentimentData?.sentiment_trends?.last_month}%
                    </div>
                    <p className="text-sm text-gray-300">{t('results.sentiment.monthlyTrend')}</p>
                  </div>
                </div>
                
                {/* Indicadores de engagement */}
                {sentimentData?.engagement_analysis?.viral_indicators && (
                  <div className="mt-3 text-center">
                    <div className="text-sm text-gray-400">
                      Potencial viral: <span className="text-cyan-300 font-semibold">
                        {sentimentData.engagement_analysis.viral_indicators.viral_potential}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      Engagement rate: {sentimentData.engagement_analysis.viral_indicators.engagement_rate}%
                    </div>
                  </div>
                )}
              </div>

              {/* Palabras clave trending mejoradas */}
              <div className="bg-slate-700/30 p-4 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-3">🔥 {t('results.labels.mostMentioned')}</h4>
                <div className="flex flex-wrap gap-2">
                  {sentimentData?.trending_keywords?.slice(0, 4).map((keyword, index) => (
                    <div 
                      key={index}
                      className="bg-cyan-500/20 text-cyan-300 px-2 py-1 rounded text-xs flex items-center gap-1"
                      title={`Frecuencia: ${keyword.frequency}`}
                    >
                      <span>{keyword.word}</span>
                      <span className="text-cyan-400 font-bold">({keyword.frequency})</span>
                    </div>
                  ))}
                </div>
                
                {/* Mostrar plataformas analizadas */}
                {sentimentData?.analysis_metadata?.platforms_covered && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-400 mb-1">{t('results.sentiment.platformsAnalyzed')}</div>
                    <div className="flex gap-2">
                      {(() => {
                        // Import platform detection helper
                        const { detectPlatformsFromLinks } = require('./utils/socialPlatforms');
                        const platforms = detectPlatformsFromLinks(formData);
                        const activePlatforms = Object.entries(platforms).filter(([_, isActive]) => isActive).map(([name]) => name);
                        
                        // Mostrar solo plataformas que el usuario realmente proporcionó
                        const platformsToShow = sentimentData.analysis_metadata.platforms_covered
                          .filter(platform => activePlatforms.includes(platform.toLowerCase()) || 
                                             ['instagram', 'tiktok'].includes(platform.toLowerCase()));
                        
                        return platformsToShow.map((platform) => (
                          <span key={platform} className="text-xs bg-slate-600/50 text-gray-300 px-2 py-1 rounded capitalize">
                            {platform}
                          </span>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Engagement por plataforma */}
            {sentimentData?.engagement_analysis?.platform_performance && (
              <div className="mt-6 bg-slate-700/30 p-4 rounded-lg">
                <h4 className="font-semibold text-cyan-400 mb-3">📱 Performance por Plataforma</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(() => {
                    // Import platform detection helper
                    const { detectPlatformsFromLinks } = require('./utils/socialPlatforms');
                    const platforms = detectPlatformsFromLinks(formData);
                    const activePlatforms = Object.entries(platforms).filter(([_, isActive]) => isActive).map(([name]) => name);
                    
                    // Filtrar performance solo de plataformas activas
                    const performanceEntries = Object.entries(sentimentData.engagement_analysis.platform_performance)
                      .filter(([platform]) => {
                        // Mostrar solo si la plataforma está activa o es una de las principales
                        return activePlatforms.includes(platform.toLowerCase()) || 
                               ['instagram', 'tiktok'].includes(platform.toLowerCase());
                      });
                    
                    return performanceEntries.map(([platform, metrics]) => (
                      <div key={platform} className="text-center">
                        <div className="text-sm font-semibold text-white capitalize">{platform}</div>
                        <div className="text-xs text-gray-400">Engagement: {metrics.engagement_rate}%</div>
                        <div className="text-xs text-gray-400">Alcance: {metrics.reach}</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Impacto en valoración mejorado - Solo con datos reales */}
            <div className="mt-4 bg-gradient-to-r from-green-600/10 to-cyan-600/10 rounded-lg p-4 border border-green-500/20">
              <h4 className="font-semibold text-green-400 mb-2 flex items-center">
                <span className="text-sm mr-1">💰</span>
                {t('results.sections.impact')}
              </h4>
              <p className="text-sm text-gray-300">
                {t('results.sentiment.impactDescription', { 
                  sentiment: sentimentData?.impact_on_brand_value?.description?.toLowerCase() || 'positive' 
                })} {' '}
                <strong className="text-green-400">
                  {sentimentData?.impact_on_brand_value?.percentage_impact > 0 ? '+' : ''}{sentimentData?.impact_on_brand_value?.percentage_impact}%{' '}
                  ({formatCurrencyLocale(Math.round(totalValue * sentimentData?.impact_on_brand_value?.percentage_impact / 100))})
                </strong>{' '}
                {t('results.sentiment.toTotalValuation')}
              </p>
              <div className="mt-2 text-xs text-gray-400">
                {t('results.sentiment.analysisConfidence')}: <span className="text-cyan-300 font-semibold">
                  {sentimentData?.confidence_level}
                </span>
                {sentimentData?.analysis_metadata?.mention_volume && (
                  <> • {t('results.sentiment.mentionsAnalyzed')}: <span className="text-cyan-300 font-semibold">
                    {sentimentData.analysis_metadata.mention_volume}
                  </span></>
                )}
              </div>
            </div>

            {/* Recomendaciones específicas mejoradas */}
            {(() => {
              const { normalizeRecommendations } = require('./utils/sentiment');
              const recs = normalizeRecommendations(sentimentData?.recommendations);
              
              return (
                <div className="mt-4 bg-slate-700/20 p-4 rounded-lg">
                  <h4 className="font-semibold text-white mb-3">💡 {t('sentiment.recommendations.title')}</h4>
                  {recs.length === 0 ? (
                    <p className="text-gray-400 text-sm">{t('sentiment.recommendations.empty')}</p>
                  ) : (
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
                      {recs.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}

            {/* Metadata del análisis */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-400">
                {t('results.sentiment.footer', { 
                  count: sentimentData?.analysis_metadata?.posts_analyzed,
                  date: new Date(sentimentData?.analysis_metadata?.analysis_date).toLocaleDateString()
                })}
              </p>
            </div>
          </>
        ) : (
          // Empty state when no real sentiment data
          <div className="text-center py-8">
            <div className="text-6xl mb-4">📊</div>
            <h4 className="text-xl font-bold text-white mb-2">
              {t('results.labels.emptyState.title')}
            </h4>
            <p className="text-gray-300 mb-6 max-w-md mx-auto">
              {t('results.labels.emptyState.subtitle')}
            </p>
            
            <div className="bg-slate-700/30 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <h5 className="text-sm font-semibold text-cyan-400 mb-3">✨ Obtendrás:</h5>
              <ul className="text-sm text-gray-300 space-y-2 text-left">
                {tArray('results.labels.emptyState.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-400 mr-2">•</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            
            <button 
              onClick={() => setCurrentStep('step4')}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all duration-300"
            >
              {t('results.labels.emptyState.cta')}
            </button>
            
            <p className="text-xs text-gray-400 mt-4">
              {t('results.labels.emptyState.note')}
            </p>
          </div>
        )}
      </div>

      {/* MÓDULO BENCHMARKING COMPETITIVO - Análisis completo con AI */}
      <BenchmarkingModule 
        result={results}
        formData={formData}
      />

      {/* Análisis Competitivo Personalizado */}
      <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-2xl p-6 border border-cyan-500/30">
        <h3 className="text-xl font-bold text-white mb-4">🎯 {t('results.sections.competitivePosition') || 'Tu Posición Competitiva'}</h3>
        {(() => {
          const personalizedInsights = generatePersonalizedInsights(formData, results, brandType, language);
          
          return (
            <div className="bg-slate-700/30 p-4 rounded-lg">
              <p className="text-gray-300 leading-relaxed">
                {personalizedInsights.competitiveAnalysis}
              </p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">
                    {brandType === 'personal' ? '85%' : '78%'}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {language === 'es' ? 'Precisión del análisis' : 'Analysis accuracy'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">
                    {totalValue > 200000 ? 'TOP 20%' : 'PROMEDIO'}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {language === 'es' ? 'Posición sectorial' : 'Sector position'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">
                    {parseFloat(formData.clients) > 100 ? 'ALTO' : parseFloat(formData.clients) > 20 ? 'MEDIO' : 'INICIAL'}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {language === 'es' ? 'Potencial crecimiento' : 'Growth potential'}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Proyecciones de crecimiento personalizadas */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
        <h3 className="text-xl font-bold text-white mb-4">📈 {t('results.sections.growthProjections')}</h3>
        
        {(() => {
          const personalizedInsights = generatePersonalizedInsights(formData, results, brandType, language);
          
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Escenario conservador personalizado */}
              <div className="bg-slate-700/30 p-4 rounded-lg text-center">
                <h4 className="font-semibold text-blue-400 mb-3">📊 {personalizedInsights.projectionInsights.conservative.title}</h4>
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-white">{formatCurrencyLocale(Math.round(totalValue * 1.15))}</div>
                  <p className="text-sm text-gray-300">{t('results.projection.in12months')}</p>
                  <div className="text-green-400 font-semibold">+15%</div>
                </div>
                <div className="mt-3 text-xs text-gray-400 space-y-1">
                  {personalizedInsights.projectionInsights.conservative.bullets.map((bullet, index) => (
                    <p key={index}>• {bullet}</p>
                  ))}
                </div>
              </div>

              {/* Escenario optimista personalizado */}
              <div className="bg-slate-700/30 p-4 rounded-lg text-center border border-indigo-500/30">
                <h4 className="font-semibold text-indigo-400 mb-3">🎯 {personalizedInsights.projectionInsights.optimistic.title}</h4>
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-white">{formatCurrencyLocale(Math.round(totalValue * 1.35))}</div>
                  <p className="text-sm text-gray-300">{t('results.projection.in12months')}</p>
                  <div className="text-green-400 font-semibold">+35%</div>
                </div>
                <div className="mt-3 text-xs text-gray-400 space-y-1">
                  {personalizedInsights.projectionInsights.optimistic.bullets.map((bullet, index) => (
                    <p key={index}>• {bullet}</p>
                  ))}
                </div>
              </div>

              {/* Escenario agresivo personalizado */}
              <div className="bg-slate-700/30 p-4 rounded-lg text-center">
                <h4 className="font-semibold text-purple-400 mb-3">🚀 {personalizedInsights.projectionInsights.aggressive.title}</h4>
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-white">{formatCurrencyLocale(Math.round(totalValue * 1.65))}</div>
                  <p className="text-sm text-gray-300">{t('results.projection.in12months')}</p>
                  <div className="text-green-400 font-semibold">+65%</div>
                </div>
                <div className="mt-3 text-xs text-gray-400 space-y-1">
                  {personalizedInsights.projectionInsights.aggressive.bullets.map((bullet, index) => (
                    <p key={index}>• {bullet}</p>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        <div className="mt-6 bg-indigo-600/10 p-4 rounded-lg border border-indigo-500/20">
          <h4 className="font-semibold text-indigo-400 mb-2">💡 {t('results.sections.keyGrowthFactors')}</h4>
          {(() => {
            const personalizedInsights = generatePersonalizedInsights(formData, results, brandType, language);
            
            return (
              <div className="space-y-2 text-sm text-gray-300">
                {personalizedInsights.keyFactors.length > 0 ? (
                  personalizedInsights.keyFactors.map((factor, index) => (
                    <p key={index}>{factor}</p>
                  ))
                ) : (
                  <>
                    <p>• {t('results.projection.keyFactors.factor1')}</p>
                    <p>• {t('results.projection.keyFactors.factor2')}</p>
                    <p>• {t('results.projection.keyFactors.factor3')}</p>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Resumen del cálculo */}
      <div className="bg-gradient-to-r from-slate-700/50 to-slate-600/50 p-6 rounded-xl border border-slate-500/30 mt-6">
        <h4 className="text-lg font-bold text-white mb-3 text-center">📊 {t('results.sections.valuationFormula')}</h4>
        <div className="text-center space-y-2">
          <p className="text-gray-300">
            {t('results.projection.formulaEquation')}
          </p>
          <p className="text-sm text-gray-400">
            {formatCurrencyLocale(baseRevenue)} × {multiplierValue} = {formatCurrencyLocale(totalValue)}
          </p>
        </div>
      </div>

      {/* MÓDULO IA PREDICTIVA - Proyección a 5 años */}
      <PredictiveModule 
        result={results}
        formData={formData}
      />

      {/* CTA para asesoría */}
      <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 rounded-xl p-6 border border-yellow-500/30 text-center">
        <div className="text-4xl mb-3">💡</div>
        <h4 className="text-xl font-bold text-white mb-3">{t('results.sections.maximizeValue')}</h4>
        <p className="text-gray-300 mb-6">
          {t('results.advisory.ctaBody')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a 
            href="https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value?utm_source=ybv&utm_medium=site&utm_campaign=free_session"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-4 rounded-xl font-bold hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-yellow-500/25 flex items-center gap-2
              sm:px-8 sm:py-4
              max-sm:px-4 max-sm:py-2.5 max-sm:text-sm max-sm:rounded-lg max-sm:font-semibold
              max-sm:[&>span]:mr-1 max-sm:[&>span]:text-base
              [&>span]:inline-block [&>span]:whitespace-nowrap
              max-sm:whitespace-nowrap max-sm:flex-nowrap"
          >
            <span className="whitespace-nowrap flex items-center">📅</span>
            <span className="whitespace-nowrap">{t('results.buttons.bookAdvisory')}</span>
          </a>
          <div className="text-center">
            <p className="text-sm text-gray-400">✅ {t('results.advisory.freeMinutes')}</p>
            <p className="text-sm text-gray-400">✅ {t('results.advisory.personalizedPlan')}</p>
          </div>
        </div>
      </div>

      {/* Recomendaciones personalizadas avanzadas */}
      <div className="bg-gradient-to-r from-green-600/20 to-blue-600/20 rounded-2xl p-6 border border-green-500/30">
        <h3 className="text-xl font-bold text-white mb-4">💡 {t('results.sections.personalizedRecommendations')}</h3>
        <div className="space-y-4">
          {generatePersonalizedRecommendations(formData, results, brandType, t).map((recommendation, index) => (
            <div key={index} className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-start space-x-3">
                <div className="text-2xl flex-shrink-0" style={{lineHeight: '1'}}>
                  {recommendation.icon}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    {recommendation.title}
                    {recommendation.priority === 'high' && (
                      <span className="ml-2 px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded-full border border-red-500/30">
                        {language === 'es' ? 'Alta Prioridad' : 'High Priority'}
                      </span>
                    )}
                    {recommendation.priority === 'medium' && (
                      <span className="ml-2 px-2 py-1 text-xs bg-yellow-500/20 text-yellow-400 rounded-full border border-yellow-500/30">
                        {language === 'es' ? 'Prioridad Media' : 'Medium Priority'}
                      </span>
                    )}
                    {recommendation.priority === 'low' && (
                      <span className="ml-2 px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded-full border border-green-500/30">
                        {language === 'es' ? 'Prioridad Baja' : 'Low Priority'}
                      </span>
                    )}
                  </h4>
                  <p 
                    className="text-gray-300 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: recommendation.text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Nota explicativa */}
        <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
          <p className="text-blue-300 text-xs">
            💡 {brandType === 'company' ? 
              (language === 'es' ? 
                `Recomendaciones personalizadas basadas en tus datos específicos: ${parseFloat(formData.clients) || 0} clientes, ${(parseFloat(formData.revenue) || 0).toLocaleString()}€ facturación, ${formData.industry || 'sector'}.` :
                `Personalised recommendations based on your specific data: ${parseFloat(formData.clients) || 0} clients, €${(parseFloat(formData.revenue) || 0).toLocaleString()} revenue, ${formData.industry || 'sector'}.`
              ) :
              (language === 'es' ? 
                `Recomendaciones personalizadas basadas en tus datos específicos: ${parseFloat(formData.clients) || 0} clientes, ${(parseFloat(formData.personalRevenue) || 0).toLocaleString()}€ ingresos, ${formData.personalNiche || 'nicho'}.` :
                `Personalised recommendations based on your specific data: ${parseFloat(formData.clients) || 0} clients, €${(parseFloat(formData.personalRevenue) || 0).toLocaleString()} income, ${formData.personalNiche || 'niche'}.`
              )
            }
          </p>
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={() => {
            localStorage.removeItem('calculatorState');
            setCurrentStep('lead');
            setResults(null);
            setLeadData({ name: '', email: '' });
            setLeadId(null);
            setBrandType('');
            setFormData({
              companyName: '', personalName: '', revenue: '', personalRevenue: '',
              industry: '', personalNiche: '', clients: '', yearsInMarket: '',
              socialFollowers: '', monthlyTraffic: '', tangibleAssets: '',
              brandRecognition: '', marketPosition: '',
              twitterProfile: '', instagramProfile: '', tiktokProfile: '', facebookProfile: '',
              linkedinProfile: '', hashtags: '', sentimentConsent: false
            });
            setValidationErrors({});
          }}
          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 font-semibold"
        >
          {t('results.buttons.calculateAnother')}
        </button>
        
        <button 
          onClick={() => setShowShareModal(true)}
          className="border-2 border-purple-500 text-purple-400 px-8 py-3 rounded-xl hover:bg-purple-500/20 transition-all duration-300 font-semibold flex items-center gap-2
            sm:px-8 sm:py-3
            max-sm:px-4 max-sm:py-2.5 max-sm:text-sm max-sm:rounded-lg max-sm:font-semibold
            max-sm:justify-center max-sm:[&>span]:mr-1 max-sm:[&>span]:text-base
            [&>span]:inline-block [&>span]:whitespace-nowrap
            max-sm:whitespace-nowrap max-sm:flex-nowrap"
        >
          <span className="whitespace-nowrap flex items-center">📤</span>
          <span className="whitespace-nowrap">{t('results.buttons.shareResult')}</span>
        </button>
      </div>
    </div>
  );
});

// Función para generar comparación sectorial (CON VERIFICACIONES SEGURAS)

  // Botones de navegación
  const NavigationButtons = () => {
    const canGoNext = () => {
      if (currentStep === 'step1') {
        const errors = validateCurrentStep();
        return Object.keys(errors).length === 0;
      }
      return true;
    };

    const showNext = ['step1', 'step2', 'step3'].includes(currentStep);
    const showPrevious = ['step1', 'step2', 'step3', 'step4'].includes(currentStep);
    const showCalculate = currentStep === 'step4';

    return (
      <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-700">
        <div>
          {showPrevious && (
            <button
              onClick={prevStep}
              className="flex items-center space-x-2 px-6 py-3 text-gray-400 hover:text-white border border-gray-600 rounded-xl hover:border-gray-500 transition-all duration-300"
            >
              <span>←</span>
              <span>{t('home.calculator.navigation.previous')}</span>
            </button>
          )}
        </div>

        <div>
          {showNext && (
            <button
              onClick={nextStep}
              disabled={!canGoNext()}
              className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{t('home.calculator.navigation.next')}</span>
              <span>→</span>
            </button>
          )}
          
          {showCalculate && (
            <button
              onClick={handleCalculate}
              className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-300"
            >
              <span>🧮</span>
              <span>{t('home.calculator.buttons.calculateBrandValue')}</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-20 sm:pt-24 pb-8 sm:pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {currentStep === 'lead' && (
          <LeadCaptureForm 
            leadData={leadData}
            setLeadData={setLeadData}
            setLeadId={setLeadId}
            setCurrentStep={setCurrentStep}
          />
        )}
        
        {currentStep === 'brandType' && <BrandTypeSelector />}
        
        {(['step1', 'step2', 'step3', 'step4', 'results'].includes(currentStep)) && (
          <div className="space-y-8">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-white/10">
              {/* Indicador de progreso */}
              {currentStep !== 'results' && <ProgressIndicator />}
              
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">
                {brandType === 'company' ? t('home.calculator.sections.businessValuation') : t('home.calculator.sections.personalBrandValuation')}
              </h2>

              {/* Disclaimer de Estimación Orientativa - Only show in steps, not in results */}
              {currentStep !== 'results' && (
                <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-xl">
                  <div className="flex items-start">
                    <div className="text-yellow-400 text-xl mr-3 mt-1">⚠️</div>
                    <div>
                      <h4 className="text-yellow-300 font-semibold text-sm mb-1">
                        {t('home.calculator.disclaimerTitle') || 'Estimación Orientativa'}
                      </h4>
                      <p className="text-yellow-100 text-sm leading-relaxed">
                        {t('home.calculator.disclaimerText') || 'Esta herramienta proporciona una estimación orientativa basada en metodología profesional. Los resultados sirven como punto de partida para la reflexión estratégica, no constituyen una valoración oficial.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="min-h-[300px] sm:min-h-[400px]">
                {isCalculating || isAnalyzingSentiment ? (
                  <div className="text-center py-20">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-500 mx-auto mb-4"></div>
                    <p className="text-white text-lg">
                      {isAnalyzingSentiment ? 'Analizando sentimiento en redes sociales...' : 'Calculando valor de marca...'}
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      {isAnalyzingSentiment ? 'Procesando datos de redes sociales...' : 'Analizando todos los factores de valor...'}
                    </p>
                  </div>
                ) : currentStep === 'results' ? (
                  <ResultsComponent 
                    results={results}
                    sentimentData={sentimentData}
                    brandType={brandType}
                    isCalculating={isCalculating}
                    formatNumber={formatNumber}
                    formData={formData}
                    setCurrentStep={setCurrentStep}
                    setResults={setResults}
                    setLeadData={setLeadData}
                    setLeadId={setLeadId}
                    setBrandType={setBrandType}
                    setFormData={setFormData}
                    setValidationErrors={setValidationErrors}
                  />
                ) : currentStep === 'step1' ? (
                  <Step1Component 
                    brandType={brandType}
                    formData={formData}
                    validationErrors={validationErrors}
                    tooltips={tooltips}
                    onTextChange={onTextChange}
                    onNumericChange={onNumericChange}
                    onSelectChange={onSelectChange}
                    t={t}
                  />
                ) : currentStep === 'step2' ? (
                  <Step2Component 
                    brandType={brandType}
                    formData={formData}
                    handleNumericInput={handleNumericInput}
                    handleTextInput={handleTextInput}
                    handleSelectChange={handleSelectChange}
                    tooltips={tooltips}
                    t={t}
                  />
                ) : currentStep === 'step3' ? (
                  <Step3Component 
                    brandType={brandType}
                    formData={formData}
                    handleNumericInput={handleNumericInput}
                    handleSelectChange={handleSelectChange}
                    tooltips={tooltips}
                    validationErrors={validationErrors}
                    t={t}
                  />
                ) : currentStep === 'step4' ? (
                  <Step4Component 
                    formData={formData}
                    handleTextInput={handleTextInput}
                    tooltips={tooltips}
                    validationErrors={validationErrors}
                    t={t}
                  />
                ) : null}
              </div>
              
              {!isCalculating && !isAnalyzingSentiment && currentStep !== 'results' && <NavigationButtons />}
            </div>
          </div>
        )}
      </div>

      {/* Modal de Compartir en Redes Sociales - ARREGLADO PARA EVITAR RECORTES */}
      {showShareModal && (
        <div className="shareBackdrop fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="shareModal bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl max-w-md w-full max-h-[min(85vh,720px)] overflow-auto p-6 border border-purple-500/30 shadow-2xl my-8">
            {/* Header del Modal */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">🎉 {t('results.shareModal.title')}</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-white text-2xl font-bold transition-colors flex-shrink-0 ml-2"
                aria-label={t('results.shareModal.close')}
              >
                ×
              </button>
            </div>

            {/* Insignia del Resultado */}
            {results && (
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-6 mb-6 text-center">
                <div className="text-4xl mb-2">💎</div>
                <h4 className="text-xl font-bold text-white mb-1">
                  {brandType === 'personal' 
                    ? t('results.shareModal.card.personal', { value: formatMoney(results.totalValue, language) })
                    : t('results.shareModal.card.company', { value: formatMoney(results.totalValue, language) })
                  }
                </h4>
                <p className="text-gray-300 text-sm">
                  {brandType === 'company' ? formData.companyName : formData.personalName}
                </p>
                <p className="text-purple-400 text-sm font-medium mt-2">
                  {t('results.shareModal.card.footer')}
                </p>
              </div>
            )}

            {/* 4 Botones de Compartir */}
            <div className="space-y-3 mb-6">
              <button
                onClick={shareX}
                disabled={isCreatingShareUrl}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 disabled:opacity-50 disabled:transform-none"
              >
                <span className="text-xl">🐦</span>
                {isCreatingShareUrl ? t('results.shareModal.preparing') : t('results.shareModal.buttons.x')}
              </button>

              <button
                onClick={shareLinkedIn}
                disabled={isCreatingShareUrl}
                className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 disabled:opacity-50 disabled:transform-none"
              >
                <span className="text-xl">💼</span>
                {isCreatingShareUrl ? t('results.shareModal.preparing') : t('results.shareModal.buttons.linkedin')}
              </button>

              <button
                onClick={shareWhatsApp}
                disabled={isCreatingShareUrl}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 disabled:opacity-50 disabled:transform-none"
              >
                <span className="text-xl">💬</span>
                {isCreatingShareUrl ? t('results.shareModal.preparing') : t('results.shareModal.buttons.whatsapp')}
              </button>

              <button
                onClick={shareNative}
                disabled={isCreatingShareUrl}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 disabled:opacity-50 disabled:transform-none"
              >
                <span className="text-xl">📤</span>
                {isCreatingShareUrl ? t('results.shareModal.preparing') : (navigator.share ? t('results.shareModal.buttons.copy') : t('results.shareModal.buttons.copy'))}
              </button>
            </div>

            {/* Footer del Modal */}
            <div className="text-center">
              <p className="text-gray-400 text-sm">
                {t('results.shareModal.helper')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Toast de confirmación de copia */}
      {showCopyToast && (
        <div className="fixed top-4 right-4 z-[9999] bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl shadow-lg transform transition-all duration-300 animate-pulse border border-green-400/20">
          <div className="flex items-center gap-3">
            <span className="text-xl">✅</span>
            <span className="font-semibold">{t('results.shareModal.copied')}</span>
          </div>
        </div>
      )}
      
      {/* Explicación del Método */}
      <div className="mt-4 p-3 bg-slate-700/30 rounded-lg border border-cyan-500/20">
        <p className="text-cyan-300 text-sm text-center">
          {t('home.calculator.methodologyExplanation') || '🧠 Cómo calculamos: IA entrenada + tus datos específicos + comparables sectoriales = estimación personalizada'}
        </p>
      </div>
    </div>
  );
};

export default ImprovedCalculator;